import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useInRouterContext } from "react-router-dom";

import {
  borrarPlanilla,
  CONFLICTO,
  crearPlanilla,
  guardarCompartir,
  guardarPlanilla,
  miMail,
  misPlanillas,
  MUY_GRANDE,
  traerPlanilla,
} from "../services/espacio";
import { crearAutomatizacion, misAvisosDePlanilla, prenderApagar } from "../services/automatizaciones";
import { avisoDeRango, celdaEnRangos, rangosVigilados, textoDeRango } from "../shared/automatizaciones";
import { CARGANDO, colALetra, ERROR_HUB, evaluar, matrizVacia, refDe } from "../shared/planilla";
import { crearHub, FUNCIONES_HUB, TTL_MS } from "../shared/planillaHub";
import * as datosDelHub from "../services/hubDatos";
import { exportarLibro } from "../shared/planillaExcel";
import { ACEPTA, avisoDeHojasNuevas, LEER_MAL, leerExcel, nombreDeArchivo, sumarHojas, textoArchivoGrande, textoMuyGrande } from "../shared/importarExcel";
import {
  anclaDe, bordeAlMover, celdaCombinada, combinarYCentrar, mapaCombinadas, normalizarCombinadas, queHaceCombinar,
  rangoElegido, revisarAntesDeCombinar, seBorranAlCombinar, separarCombinada, textoAlCombinar, textoCruce,
} from "../shared/planillaCombinadas";
import { FONDOS, TEXTOS } from "../shared/paletaPlanilla";
import { calcularFormato, normalizarReglas, normalizarValidaciones } from "../shared/formatoCondicional";
import { normalizarTablasHub } from "../shared/tablasHub";
import {
  anotarPropio, DUENO, EDITOR, esSinAcceso, idDelLink, linkDePlanilla, marcarEnLaUrl, rolEnPlanilla,
} from "../shared/espacioAcceso";
import {
  ALTERNOS,
  aplicarBordes,
  aTSV,
  autoSuma,
  borrarRango,
  buscar,
  colorAlterno,
  cambiarEstructura,
  copiarRango,
  deTSV,
  dividirTexto,
  escribir,
  esOscuro,
  formatear,
  FORMATOS_NUMERO,
  ordenar,
  pegar,
  pegarFormato,
  quitarDuplicados,
  recortarEspacios,
  reemplazar,
  rellenar,
  TIPOS_BORDE,
} from "../shared/planillaOps";
import AsistenteFormula from "./AsistenteFormula";
import BarraMenus from "./BarraMenus";
import CompartirPanel from "./CompartirPanel";
import HistorialPlanilla from "./HistorialPlanilla";
import { BotonImportarExcel, SoltarExcel } from "./ImportarExcel";
import ImportacionesBot from "./ImportacionesBot";
import PapeleraPlanillas from "./PapeleraPlanillas";
import InsertarTablaHub from "./InsertarTablaHub";
import Loading from "./Loading";
import { BotonesReglas, MenuLista, PanelReglas } from "./PanelCondicional";
import PlantillasPlanilla from "./PlantillasPlanilla";
import { AvisoEnVivo, PENDIENTE, PlanillaSinAcceso, usePlanillaEnVivo } from "./PlanillaEnVivo";

import { areaUsada, AVISO_LETRA_MINIMA, imprimirHtml, papelParaImprimir } from "../shared/planillaImprimir";
import {
  copiarImagen, copiarTextoYTabla, dibujarSeleccion, hayLienzo, IMAGEN_COPIADA, IMAGEN_FALLO, IMAGEN_MUY_GRANDE,
  IMAGEN_NO_SE_COPIA, IMAGEN_SIN_LIENZO, nombreDeImagen, seleccionConFormato, tablaParaCopiar,
} from "../shared/planillaCopiar";

import "./css/espacio.css";
import { TOPE_IMPORTAR_BYTES, TOPE_PLANILLA_MB } from "../shared/topes";

/* Mi espacio: el rincón de cada uno dentro del hub.
 *
 * Cada persona arma sus propias planillas —estilo Excel, con fórmulas— para
 * llevar el registro de lo que quiera. Es suyo (la RLS lo scopea por mail) y
 * nosotros nos alimentamos de ese dato desde el servidor.
 *
 * ── SE USA COMO UN EXCEL ───────────────────────────────────────────────────
 *
 * Para que alguien deje su Excel, la planilla tiene que responder a lo que sus
 * manos ya saben. Por eso una celda tiene DOS modos, como allá:
 *
 *   · ELEGIDA: las flechas, Enter y Tab se mueven; escribir pisa lo que había;
 *     Suprimir borra; Ctrl+C/X/V copian, cortan y pegan rangos enteros.
 *   · EDITANDO (doble clic, F2, o empezar a escribir): las flechas mueven el
 *     cursor adentro del texto y Escape deja la celda como estaba.
 *
 * Cada celda sigue siendo un `<input>`: así el teclado del teléfono, los
 * acentos y el dictado andan solos. Lo que cambia es quién manda el teclado.
 *
 * Las operaciones que tocan datos (pegar, rellenar, insertar, ordenar) viven en
 * `shared/planillaOps`, puras y probadas aparte. Acá sólo se decide cuándo
 * llamarlas. Todo cambio pasa por `mutar`, que es lo que hace que se pueda
 * deshacer.
 *
 * ── VARIAS HOJAS (PESTAÑAS) ─────────────────────────────────────────────────
 *
 * Una planilla es un LIBRO con una o más hojas, como un Excel. En la base se
 * guarda `datos = { hojas: [{ nombre, celdas, formato, anchos, altos, fija }],
 * activa }`. Las de antes guardaban un solo grid plano (`{ celdas, formato }`);
 * se abren como "Hoja 1" sin migrar nada (ver `normalizarLibro`). Una hoja
 * puede traer además `condicional` (formato condicional) y `validacion`
 * (listas desplegables): la lógica y el formato, en `shared/formatoCondicional`;
 * el panel y la lista que se abre, en `PanelCondicional`.
 *
 * ── COMPARTIR ──────────────────────────────────────────────────────────────
 *
 * El dueño puede compartir una planilla con compañeros: ellos la LEEN (no la
 * editan — una sola mano escribe). Y CEPI aparece SIEMPRE tildado y bloqueado:
 * no es un destinatario más de la lista (ya leemos todo por el servidor), es el
 * aviso honesto de que lo que subís nos alimenta. El disclaimer lo explica.
 *
 * ── LA CAMPANITA ─────────────────────────────────────────────────────────
 *
 * Igna, 03/10/2026: «Avisarme por WhatsApp si esto cambia» sobre lo
 * seleccionado. Arma una automatización (ver `routes/Automatizaciones.jsx`)
 * que mira ese rango y le manda un WhatsApp al dueño cuando cambia. Va en el
 * botón derecho y en la barra de herramientas, y sólo en planillas PROPIAS:
 * una automatización sólo puede mirar lo del dueño. Las celdas vigiladas
 * llevan una campanita chiquita, y arriba de la grilla se listan los avisos
 * de la hoja con un link a la automatización y un «Apagar», para que la
 * campanita no sea algo que se prende y después no se sabe dónde se apaga.
 *
 * ── LOS DATOS DEL HUB (HUB.…) ─────────────────────────────────────────────
 *
 * Igna, 04/10/2026: =HUB.PRECIO_GASOIL(), =HUB.VIAJES(…) y compañía traen
 * datos vivos del hub (la lista y las reglas, en `shared/planillaHub`). Cada
 * planilla abierta tiene su propio `hub` con su caché: se crea con el editor y
 * se cierra con él. Mientras un dato viaja la celda muestra «…»; cuando llega,
 * `alLlegar` sube `hubVersion` y la hoja se recalcula. Se lee con la sesión
 * de quien MIRA: en una planilla compartida cada uno ve lo que su permiso le
 * deja ver. El bot de los avisos no tiene sesión: para él valen #HUB.
 */

const FILAS_NUEVA = 100; // 1 a 100
const COLS_NUEVA = 26; // A a Z

const empaquetar = (h) => ({
  filas: h.celdas.length,
  cols: h.celdas[0] ? h.celdas[0].length : 0,
  celdas: h.celdas,
  formato: h.formato || {},
  // Ancho de las columnas que se agrandaron/achicaron a mano, en px, por índice
  // de columna. Vacío = todas al ancho por defecto. Se guarda para que el
  // tamaño de columna sobreviva al recargar, igual que el formato.
  anchos: h.anchos || {},
  // Alto de las filas redimensionadas a mano, en px, por índice de fila. Vacío =
  // todas al alto por defecto (Nacho, 24/09). Mismo criterio que `anchos`.
  altos: h.altos || {},
  // Qué queda fijo al desplazarse: `{ fila: true }` la fila 1, `{ col: true }`
  // la columna A.
  fija: h.fija || {},
  // El juego de colores alternos de la hoja («azul», «gris»…), si tiene.
  ...(h.alternos ? { alternos: h.alternos } : {}),
  // Formato condicional y listas desplegables: sólo si los hay, así una hoja
  // que no los usa se guarda igual que siempre.
  ...(h.condicional && h.condicional.length ? { condicional: h.condicional } : {}),
  ...(h.validacion && h.validacion.length ? { validacion: h.validacion } : {}),
  // Las tablas del hub volcadas en la hoja, para poder «Actualizar» (ver
  // `shared/tablasHub`). Sólo si hay: una hoja sin tablas se guarda como antes.
  ...(h.tablasHub && h.tablasHub.length ? { tablasHub: h.tablasHub } : {}),
  // Celdas combinadas («A1:C1»), sólo si las hay: ver `shared/planillaCombinadas`.
  ...(h.combinadas && h.combinadas.length ? { combinadas: h.combinadas } : {}),
});

/* Ancho por defecto de una columna de datos y de la columna de números de fila
   (px). El resto de columnas arranca en el default y se agranda arrastrando el
   borde del encabezado. */
const ANCHO_COL = 96;
const ANCHO_FILA_NUM = 44;
const ANCHO_MIN = 40;
// Sin tope de ancho a propósito (Nacho, 24/09): el arrastre es libre al 100%.
const ALTO_MIN = 22;   // alto mínimo de fila (px); sin tope, como el ancho.
// El ajuste automático (doble clic en el borde) sí tiene techo: una celda con un
// párrafo adentro no puede dejar la columna de tres pantallas de ancho. A mano
// se la puede seguir estirando sin límite.
const ANCHO_AJUSTE_MAX = 480;
const RELLENO_CELDA = 18; // el padding de la celda más un respiro, en px
/* Los encabezados son `sticky`, y cada uno es su propia capa: el que viene
   después en el HTML se pinta ARRIBA del anterior. El tirador sobresale del
   borde hacia el encabezado vecino, así que esa mitad quedaba tapada y no se
   podía agarrar. Se numeran las capas al revés —el de más a la izquierda (o más
   arriba) gana— y los de columna van por encima de los de fila. */
const Z_ENCABEZADO = 1000;
const Z_FILA = 10; // los números de fila, por encima de cualquier celda (la activa llega a 3)
/* El alto de las filas del encabezado, en px. Están fijos (y repetidos en el
   CSS) porque cada fila pegada arriba necesita saber dónde termina la anterior. */
const ALTO_LETRAS = 26;
const ALTO_FILTRO = 30;
const MAX_DESHACER = 100;

/* Devuelve una función que mide, en px, cuánto ocupa un texto con la tipografía
   de la grilla. Usa un canvas (no toca el layout); donde no lo hay, estima por
   cantidad de letras. */
function medidorDeTexto(muestra) {
  let ctx = null;
  try {
    ctx = document.createElement("canvas").getContext("2d");
  } catch { ctx = null; }
  if (!ctx) return (s) => s.length * 7.5;
  const cs = muestra && window.getComputedStyle ? window.getComputedStyle(muestra) : null;
  ctx.font = cs && cs.fontSize
    ? `${cs.fontStyle || "normal"} ${cs.fontWeight || 400} ${cs.fontSize} ${cs.fontFamily || "sans-serif"}`
    : "13px sans-serif";
  return (s) => ctx.measureText(s).width;
}

/* Una hoja vacía, lista para sumar al libro. */
const hojaVacia = (nombre) => ({
  nombre, celdas: matrizVacia(FILAS_NUEVA, COLS_NUEVA), formato: {}, anchos: {}, altos: {}, fija: {},
});

/* La paleta de formato (FONDOS y TEXTOS) vive en `shared/paletaPlanilla`: la
   usa también la importación de Excel. */
const ALINEAR = [["left", "Izq"], ["center", "Cen"], ["right", "Der"]];
const TAMANOS = [9, 10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32, 36]; // px; 13 es el de la grilla
const ZOOMS = [50, 75, 90, 100, 125, 150, 200];
const CLAVE_ZOOM = "fibosheets.zoom";
/* El zoom es de quien mira, no de la planilla: se recuerda en este navegador. */
const zoomGuardado = () => {
  try {
    const z = Number(window.localStorage.getItem(CLAVE_ZOOM));
    return ZOOMS.includes(z) ? z : 100;
  } catch { return 100; }
};
const NUMEROS = [
  ["moneda", "$", "Moneda: $ 1.234,50"],
  ["pct", "%", "Porcentaje: 0,25 se ve 25 %"],
  ["miles", "000", "Con puntos de miles: 1.234.567"],
];

/* El estilo de una celda desde su formato. Si tiene fondo, el texto va fijo
   —oscuro sobre un fondo claro, blanco sobre uno oscuro— para que se lea en
   los dos temas.

   Los bordes son sombras hacia adentro y no `border`: así no le cambian el
   tamaño a la celda ni se pelean con los bordes separados de la grilla. */
const SOMBRA_BORDE = {
  t: "inset 0 1.5px 0 var(--esp-borde)", b: "inset 0 -1.5px 0 var(--esp-borde)",
  l: "inset 1.5px 0 0 var(--esp-borde)", r: "inset -1.5px 0 0 var(--esp-borde)",
};
function estiloCelda(fmt) {
  if (!fmt) return {};
  const td = {};
  if (fmt.bg) td.backgroundColor = fmt.bg;
  // Alineación vertical: se nota en las filas más altas que el texto. El
  // `<input>` deja de ocupar todo el alto para que la celda lo pueda ubicar.
  if (fmt.valign) td.verticalAlign = fmt.valign;
  if (fmt.bordes) {
    const sombras = Object.keys(SOMBRA_BORDE).filter((k) => fmt.bordes[k]).map((k) => SOMBRA_BORDE[k]);
    if (sombras.length) td.boxShadow = sombras.join(", ");
  }
  const decoracion = [fmt.subrayado && "underline", fmt.tachado && "line-through"].filter(Boolean).join(" ");
  return {
    td: Object.keys(td).length ? td : undefined,
    input: {
      color: fmt.color || (fmt.bg ? (esOscuro(fmt.bg) ? "#ffffff" : "#17212b") : undefined),
      fontWeight: fmt.bold ? 700 : undefined,
      fontStyle: fmt.italic ? "italic" : undefined,
      textDecoration: decoracion || undefined,
      textAlign: fmt.align || undefined,
      fontSize: fmt.size ? `${fmt.size}px` : undefined,
      height: fmt.valign ? "auto" : undefined,
    },
  };
}

/* El `<input>` de una celda, si está dibujado (la virtualización sólo monta las
   filas que se ven). */
const inputEn = (tabla, r, c) =>
  (tabla ? tabla.querySelector(`input[aria-label="${refDe(r, c)}"]`) : null);
const traerALaVista = (el) => {
  const td = el.closest("td");
  if (td && td.scrollIntoView) td.scrollIntoView({ block: "nearest", inline: "nearest" });
};
const esCeldaDeGrilla = (t) => !!(t && t.closest && t.closest("td[data-r]"));
const esTeclaDeTexto = (k) => k.length === 1 || k === "Dead" || k === "Process" || k === "Unidentified";
const sinFinDeLinea = (x) => String(x || "").replace(/\r/g, "").replace(/\n$/, "");

/* Una celda, MEMOIZADA. Es lo que hace fluida una planilla grande: al tipear en
   una celda, React sólo re-renderiza las celdas cuyo valor/estado cambió (la que
   editás y las que dependen de ella por fórmula), no las 2600. Los props son
   primitivos y `acc` —las acciones— es siempre el mismo objeto, así el memo
   corta bien.

   Muestra el valor calculado y con formato; el texto crudo (la fórmula) sólo
   mientras se la edita. */
const Celda = memo(function Celda({
  r, c, raw, val, fmt, activa, editando, enRango, enRelleno, asa, fija, soloLectura, vigilada, motivo,
  cond, lista, invalida, span = 1, tramo = null, cont = false, alt = null, altCab = false, bajar = 0, acc,
}) {
  // `cond` es el estilo de la regla de formato condicional que se cumple (las
  // mismas claves que el formato a mano, y manda sobre él). `alt` es la franja
  // de colores alternos que le toca a la fila: va por debajo de todo.
  const conFranja = alt && !(fmt && fmt.bg) ? { bg: alt, ...(altCab ? { bold: true } : {}), ...fmt } : fmt;
  const est = estiloCelda(cond ? { ...conFranja, ...cond } : conFranja);
  const nota = fmt && fmt.nota;
  // Celda combinada (ver `shared/planillaCombinadas`): el tramo de cada fila es
  // UNA celda ancha, y entre tramos de la misma combinada no va la línea de
  // abajo. Los tramos de abajo (`cont`) no tienen valor: llevan a la ancla.
  const tdStyle = tramo === "arriba" || tramo === "medio" ? { ...est.td, borderBottomColor: "transparent" } : est.td;
  // Una combinada de varias filas centrada en vertical: el texto se baja (sin
  // mover la fila) hasta la mitad del bloque, y va por encima de los tramos de
  // abajo, que pueden tener fondo.
  const inputStyle = bajar > 0 ? { ...est.input, position: "relative", top: bajar, zIndex: 1 } : est.input;
  if (cont) {
    return (
      <td
        className={"esp-comb-cont" + (enRango ? " en-rango" : "")} colSpan={span} style={tdStyle} data-r={r} data-c={c}
        onMouseDown={(e) => acc.mouseDown(r, c, e)} onMouseEnter={() => acc.mouseEnter(r, c)} onDoubleClick={() => acc.editar(r, c)}
      />
    );
  }
  const clase = [
    typeof val === "number" ? "num" : val === CARGANDO ? "cargando" : (typeof val === "string" && val.startsWith("#")) ? "err" : "",
    enRango ? "en-rango" : "",
    enRelleno ? "en-relleno" : "",
    activa ? "activa" : "",
    editando ? "editando" : "",
    fija ? "fija-col" : "",
    vigilada ? "vigilada" : "",
    lista ? "con-lista" : "",
    invalida ? "invalida" : "",
    nota ? "con-nota" : "",
  ].filter(Boolean).join(" ");
  return (
    <td
      className={clase} style={tdStyle} data-r={r} data-c={c} colSpan={span > 1 ? span : undefined}
      title={motivo || invalida || nota || (vigilada ? "Te avisamos por WhatsApp si cambia" : undefined)}
      onMouseDown={(e) => acc.mouseDown(r, c, e)}
      onMouseEnter={() => acc.mouseEnter(r, c)}
      onDoubleClick={() => acc.editar(r, c)}
    >
      <input
        value={editando ? raw : formatear(val, fmt)}
        readOnly={soloLectura} style={inputStyle} spellCheck={false} autoComplete="off"
        onFocus={() => acc.foco(r, c)}
        onChange={(e) => acc.cambio(r, c, e.target.value)}
        aria-label={refDe(r, c)}
      />
      {nota && <span className="esp-nota-marca" role="img" aria-label={`Nota: ${nota}`} />}
      {vigilada && <span className="esp-campana" aria-hidden="true">🔔</span>}
      {invalida && <span className="esp-invalida" role="img" aria-label={invalida} title={invalida}>!</span>}
      {lista && (
        <button
          type="button" className="esp-lista-flecha" tabIndex={-1} aria-label={`Elegir de la lista (${refDe(r, c)})`}
          // No se lleva el foco (ni abre el teclado del teléfono): la lista
          // elige la celda y se abre sola.
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onClick={(e) => acc.abrirLista(r, c, e.currentTarget)}
        >▾</button>
      )}
      {asa && (
        <span
          className="esp-asa" aria-hidden="true" title="Arrastrá para rellenar"
          onPointerDown={(e) => acc.asaDown(e)}
        />
      )}
    </td>
  );
});

/* ── La barra de herramientas ──────────────────────────────────────────────
 * Todo actúa sobre lo SELECCIONADO: una celda, un rango, una columna entera
 * (clic en su letra) o toda la hoja (clic en la esquina). */

function Herramientas({
  hay, act, decimales, aplicar, puedeDeshacer, puedeRehacer, deshacer, rehacer,
  avisar, bordes, brocha, tomarBrocha, combinar, comb = "nada", children,
}) {
  const off = !hay;
  return (
    /* El mousedown no se lleva el foco: después de tocar un botón, el teclado
       sigue en la celda. */
    <div
      className="esp-formato" role="toolbar" aria-label="Herramientas"
      onMouseDown={(e) => { if (e.target.closest("button")) e.preventDefault(); }}
    >
      <button className="esp-btn chico" disabled={!puedeDeshacer} onClick={deshacer} title="Deshacer (Ctrl+Z)" aria-label="Deshacer">↶</button>
      <button className="esp-btn chico" disabled={!puedeRehacer} onClick={rehacer} title="Rehacer (Ctrl+Y)" aria-label="Rehacer">↷</button>
      <button
        className={"esp-btn chico" + (brocha ? " sel" : "")} disabled={off && !brocha} onClick={tomarBrocha} aria-pressed={!!brocha}
        aria-label="Copiar formato"
        title={brocha ? "Elegí dónde pintar el formato (Esc cancela)" : "Copiar formato: tomá el de lo seleccionado y después elegí dónde ponerlo"}
      >🖌</button>
      <span className="esp-sep" />
      <span className="tenue">Fondo</span>
      {FONDOS.map((c) => (
        <button
          key={c || "none"} disabled={off} title={c || "sin color"}
          aria-label={"fondo " + (c || "ninguno")}
          className={"esp-swatch" + ((act.bg || "") === c ? " sel" : "")}
          style={{ background: c || "transparent" }}
          onClick={() => aplicar({ bg: c || undefined })}
        >{c ? "" : "∅"}</button>
      ))}
      <span className="tenue" style={{ marginLeft: 6 }}>Texto</span>
      {TEXTOS.map((c) => (
        <button
          key={c || "none"} disabled={off} title={c || "por defecto"}
          aria-label={"texto " + (c || "por defecto")}
          className={"esp-swatch letra" + ((act.color || "") === c ? " sel" : "")}
          style={{ color: c || "inherit" }}
          onClick={() => aplicar({ color: c || undefined })}
        >A</button>
      ))}
      <span className="esp-sep" />
      <button
        className={"esp-btn chico" + (act.bold ? " sel" : "")} disabled={off} title="Negrita (Ctrl+B)"
        onClick={() => aplicar({ bold: act.bold ? undefined : true })}
      ><b>B</b></button>
      <button
        className={"esp-btn chico" + (act.italic ? " sel" : "")} disabled={off} title="Itálica (Ctrl+I)"
        onClick={() => aplicar({ italic: act.italic ? undefined : true })}
      ><i>I</i></button>
      <button
        className={"esp-btn chico" + (act.subrayado ? " sel" : "")} disabled={off} title="Subrayado (Ctrl+U)"
        onClick={() => aplicar({ subrayado: act.subrayado ? undefined : true })}
      ><u>S</u></button>
      <select
        className="esp-ambito" disabled={off} aria-label="Tamaño de letra" title="Tamaño de letra"
        value={act.size || 13} onChange={(e) => aplicar({ size: Number(e.target.value) === 13 ? undefined : Number(e.target.value) })}
      >
        {TAMANOS.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <button
        className={"esp-btn chico" + (act.tachado ? " sel" : "")} disabled={off} title="Tachado (Alt+Shift+5)"
        aria-label="Tachado" onClick={() => aplicar({ tachado: act.tachado ? undefined : true })}
      ><s>T</s></button>
      {ALINEAR.map(([a, txt]) => (
        <button
          key={a} disabled={off} title={"Alinear a la " + txt.toLowerCase()}
          className={"esp-btn chico" + (act.align === a ? " sel" : "")}
          onClick={() => aplicar({ align: act.align === a ? undefined : a })}
        >{txt}</button>
      ))}
      {combinar && (
        <button
          className={"esp-btn chico" + (comb === "separar" ? " sel" : "")} disabled={off || comb === "nada"}
          onClick={combinar}
          title={comb === "separar"
            ? "Separar celdas: la combinada vuelve a ser celdas sueltas"
            : comb === "nada"
              ? "Elegí varias celdas para combinarlas"
              : "Combinar y centrar: lo elegido queda como una sola celda, con el texto al medio"}
        >{comb === "separar" ? "Separar celdas" : "Combinar y centrar"}</button>
      )}
      <span className="esp-sep" />
      {NUMEROS.map(([clave, txt, titulo]) => (
        <button
          key={clave} disabled={off} title={titulo} aria-label={titulo}
          className={"esp-btn chico" + (act.num === clave ? " sel" : "")}
          onClick={() => aplicar({ num: act.num === clave ? undefined : clave, dec: undefined })}
        >{txt}</button>
      ))}
      <button
        className="esp-btn chico" disabled={off || decimales <= 0} title="Menos decimales" aria-label="Menos decimales"
        onClick={() => aplicar({ dec: Math.max(0, decimales - 1) })}
      >,0←</button>
      <button
        className="esp-btn chico" disabled={off || decimales >= 10} title="Más decimales" aria-label="Más decimales"
        onClick={() => aplicar({ dec: Math.min(10, decimales + 1) })}
      >,00→</button>
      <span className="esp-sep" />
      <select
        className="esp-ambito" disabled={off} aria-label="Bordes" title="Bordes de lo seleccionado"
        value="" onChange={(e) => bordes(e.target.value)}
      >
        <option value="" disabled hidden>Bordes</option>
        {TIPOS_BORDE.map(([clave, nombre]) => <option key={clave} value={clave}>{nombre}</option>)}
      </select>
      {avisar && (
        <>
          <span className="esp-sep" />
          <button
            className="esp-btn chico" disabled={off} onClick={avisar}
            title="Te mandamos un WhatsApp cuando cambie algo de lo seleccionado"
          >🔔 Avisarme si cambia</button>
        </>
      )}
      {children}
    </div>
  );
}

/* Normaliza el grid guardado de UNA hoja a una matriz rectangular de strings. */
function normalizar(datos) {
  const d = datos || {};
  const base = Array.isArray(d.celdas) && d.celdas.length
    ? d.celdas
    : matrizVacia(d.filas || FILAS_NUEVA, d.cols || COLS_NUEVA);
  const cols = Math.max(1, d.cols || COLS_NUEVA, ...base.map((f) => f.length));
  return base.map((f) => {
    const g = Array.from(f, (x) => (x == null ? "" : String(x)));
    while (g.length < cols) g.push("");
    return g;
  });
}

/* Las tablas del hub de una hoja guardada, validadas (ver `shared/tablasHub`). */
const tablasDeHoja = (h) => {
  const t = normalizarTablasHub(h && h.tablasHub);
  return t.length ? { tablasHub: t } : {};
};

/* El libro guardado → lista de hojas en memoria. Compatibilidad: una planilla
   vieja (un solo grid, sin `hojas`) se abre como una única "Hoja 1". */
function normalizarLibro(datos) {
  const d = datos || {};
  const una = (h, nombre) => ({
    nombre,
    celdas: normalizar(h),
    formato: (h && h.formato) || {},
    anchos: (h && h.anchos) || {},
    altos: (h && h.altos) || {},
    fija: (h && h.fija) || {},
    ...(h && ALTERNOS[h.alternos] ? { alternos: h.alternos } : {}),
    condicional: normalizarReglas(h && h.condicional),
    validacion: normalizarValidaciones(h && h.validacion),
    ...tablasDeHoja(h),
    combinadas: normalizarCombinadas(h && h.combinadas),
  });
  if (Array.isArray(d.hojas) && d.hojas.length) {
    return d.hojas.map((h, i) => una(h, (h && h.nombre) || `Hoja ${i + 1}`));
  }
  return [una(d, "Hoja 1")];
}

/* Las hojas en memoria → lo que va a la base. */
const empaquetarLibro = (hojas, activa) => ({
  hojas: hojas.map((h) => ({ nombre: h.nombre, ...empaquetar(h) })),
  activa: activa || 0,
});

/* Lo que dice el cartelito del guardado, honesto: «Guardado» sólo cuando la
   base tiene lo último. Los malos traen además el cartel grande con qué hacer. */
const TEXTO_ESTADO = {
  cargando: "Cargando…",
  guardando: "Guardando…",
  guardado: "Guardado",
  error: "No se pudo guardar",
  grande: `No se pudo guardar: pasó el tope de ${TOPE_PLANILLA_MB} MB`,
  conflicto: "No se guardó: cambió en otro lado",
  "error-recarga": "No se guardó: cambió en otro lado",
};
const ESTADO_MALO = { error: true, grande: true, conflicto: true, "error-recarga": true };

/* Nombre de hoja válido para el Excel exportado (SheetJS): sin caracteres
   prohibidos y a lo sumo 31 caracteres. */
const nombreHojaExcel = (nombre, i) =>
  ((nombre || `Hoja ${i + 1}`).replace(/[\\/?*[\]:]/g, "-").slice(0, 31)) || `Hoja ${i + 1}`;

/* Un link a otra pantalla del hub. Mi espacio también se dibuja suelto (los
   tests lo montan sin router): ahí un `<Link>` revienta, así que cae a un
   `<a>` común, que lleva al mismo lugar recargando. */
function IrA({ to, children }) {
  const enRouter = useInRouterContext();
  return enRouter
    ? <Link className="rto-link" to={to}>{children}</Link>
    : <a className="rto-link" href={to}>{children}</a>;
}

/* ── El editor de una planilla ─────────────────────────────────────────────── */

function Editor({ id, yo, onSalir, onCambioNombre, aviso = null, onCerrarAviso }) {
  const [nombre, setNombre] = useState("");
  const [hojas, setHojasEstado] = useState(null); // [{ nombre, celdas, formato, … }]
  const [activaPedida, setActiva] = useState(0);
  const [editandoHoja, setEditandoHoja] = useState(null); // índice en renombre
  const [confirmandoBorrar, setConfirmandoBorrar] = useState(null); // índice a borrar
  const [meta, setMeta] = useState({ dueno: "", compartidaCon: [], editores: [] });
  const [sel, setSelEstado] = useState(null); // la celda activa
  const [sel2, setSel2Estado] = useState(null); // la otra esquina del rango
  const [editando, setEditandoEstado] = useState(false);
  const [relleno, setRelleno] = useState(null); // hasta dónde va el arrastre del cuadradito
  const [menu, setMenu] = useState(null); // el menú del botón derecho
  const [filtros, setFiltros] = useState({}); // { colIndex: texto }
  const [verFiltros, setVerFiltros] = useState(false);
  const [busca, setBusca] = useState("");
  const [sinResultado, setSinResultado] = useState(false);
  const [zoom, setZoom] = useState(zoomGuardado); // en %, sólo de la vista
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  const raizRef = useRef(null);
  const ayudaRef = useRef(null);
  const zoomRef = useRef(1);
  zoomRef.current = zoom / 100;
  useEffect(() => {
    const alCambiar = () => setPantallaCompleta(!!document.fullscreenElement && document.fullscreenElement === raizRef.current);
    document.addEventListener("fullscreenchange", alCambiar);
    return () => document.removeEventListener("fullscreenchange", alCambiar);
  }, []);
  const [brocha, setBrocha] = useState(null); // los formatos copiados, esperando dónde pintarlos
  const brochaRef = useRef(null);
  brochaRef.current = brocha;
  const [notaEdit, setNotaEdit] = useState(null); // { r, c, texto } con el panel abierto
  const [reemplazo, setReemplazo] = useState(null); // { buscar, por } con el panel abierto
  const [scroll, setScroll] = useState({ top: 0, alto: 600 }); // virtualización
  const [filaAlto, setFilaAlto] = useState(null); // null: todavía no se midió (vale 30)
  const [avisos, setAvisos] = useState([]); // mis automatizaciones prendidas que miran esta planilla
  const [avisoCampana, setAvisoCampana] = useState(null); // { tipo, texto } de la última campanita
  const [panelReglas, setPanelReglas] = useState(null); // "condicional" | "validacion" | null
  const [listaAbierta, setListaAbierta] = useState(null); // { r, c, x, y, ancho } de la flechita tocada
  const [avisoPropio, setAvisoPropio] = useState(null); // lo que pasó al importar un Excel como hojas nuevas
  // «Copiar como imagen» cuando el navegador no deja: { texto, url, nombre }
  // para bajarla (la `url` es un blob: que se suelta al cerrar el aviso).
  const [avisoImagen, setAvisoImagen] = useState(null);
  // { rango, hoja, seBorran }: combinar pierde algo y se pregunta. `hoja` es la
  // hoja misma (el objeto), no su lugar en el libro: ver `ponerHojas`.
  const [pedidoComb, setPedidoComb] = useState(null);
  const wrapRef = useRef(null);
  const tablaRef = useRef(null);
  const medidaRef = useRef(null); // el cartelito con los px mientras se arrastra
  const buscaRef = useRef(null);
  const barraRef = useRef(null); // la barra de fórmulas (el asistente de fórmulas la usa)
  const [estado, setEstado] = useState("cargando");
  const cargado = hojas !== null;
  /* FiboSheets #6: `esMio` es «la edito como si fuera mía»: el dueño o un
     invitado con «puede editar». Lo que es SÓLO del dueño (compartir,
     borrar, los avisos por WhatsApp —el bot los arma sólo sobre planillas
     propias—) mira `esDueno`. La base lo exige igual (RLS + trigger). */
  const rol = meta.dueno ? rolEnPlanilla(meta, yo) : null;
  const esDueno = rol === DUENO;
  const esMio = esDueno || rol === EDITOR;
  // Deshacer puede dejar el libro con menos hojas que la que estaba a la vista.
  const activa = hojas ? Math.min(activaPedida, hojas.length - 1) : 0;

  /* ── El estado que los manejadores leen AL MOMENTO ────────────────────────
   * Un manejador de teclado o de mouse corre entre un render y el siguiente, y
   * dos teclas seguidas no pueden ver el estado viejo. Lo que necesitan —la
   * selección, si se está editando, el libro— vive también en refs, que se
   * escriben junto con el estado. */
  const hojasRef = useRef(null);
  const selRef = useRef(null);
  const sel2Ref = useRef(null);
  const editRef = useRef(false);
  const activaRef = useRef(0);
  const esMioRef = useRef(false);
  activaRef.current = activa;
  esMioRef.current = esMio;

  /* Todo lo que cambia el libro pasa por acá: escribir, borrar, pegar, una
     hoja borrada (`mutar`), Ctrl+Z / Ctrl+Y y la relectura en vivo de cuando
     otro la guardó (`ponerLeida`). Si estaba abierta la pregunta de «Combinar
     y centrar», lo que nombraba puede no ser más lo que se perdería: se cierra,
     y el botón la vuelve a hacer sobre lo de ahora. */
  const ponerHojas = useCallback((n) => {
    hojasRef.current = n;
    setHojasEstado(n);
    setPedidoComb(null);
  }, []);
  const elegir = useCallback((s, s2 = null) => {
    selRef.current = s; sel2Ref.current = s2;
    setSelEstado(s); setSel2Estado(s2);
  }, []);
  const setEditando = useCallback((v) => { editRef.current = v; setEditandoEstado(v); }, []);

  /* La hoja a la vista y su contenido (derivados del libro). */
  const hoja = hojas ? hojas[activa] || hojas[0] : null;
  const celdas = hoja ? hoja.celdas : null;
  const formato = useMemo(() => (hoja ? (hoja.formato || {}) : {}), [hoja]);
  const anchos = useMemo(() => (hoja ? (hoja.anchos || {}) : {}), [hoja]);
  const altos = useMemo(() => (hoja ? (hoja.altos || {}) : {}), [hoja]);
  const fija = useMemo(() => (hoja ? (hoja.fija || {}) : {}), [hoja]);
  const combinadas = hoja ? hoja.combinadas : null;
  const mapaComb = useMemo(() => mapaCombinadas(combinadas), [combinadas]);
  // Hasta dónde hay datos: las franjas de colores alternos llegan hasta ahí.
  const usada = useMemo(() => (celdas ? areaUsada(celdas) : { filas: 0, cols: 0 }), [celdas]);

  /* ── Deshacer y rehacer ───────────────────────────────────────────────────
   * Cada cambio guarda el libro de antes. No pesa: las hojas y las filas que no
   * se tocaron son las mismas en las dos versiones. Los cambios con la misma
   * `clave` seguidos (las letras de una misma celda) cuentan como UN paso. */
  const hist = useRef({ pasado: [], futuro: [], clave: null });
  const mutar = useCallback((fn, clave = null) => {
    if (!esMioRef.current) return;
    const antes = hojasRef.current;
    if (!antes) return;
    const despues = fn(antes);
    if (!despues || despues === antes) return;
    const H = hist.current;
    if (!clave || H.clave !== clave) {
      H.pasado.push(antes);
      if (H.pasado.length > MAX_DESHACER) H.pasado.shift();
    }
    H.clave = clave;
    H.futuro = [];
    ponerHojas(despues);
  }, [ponerHojas]);
  const mutarHoja = useCallback((fn, clave = null) => mutar((hs) => {
    const i = activaRef.current;
    const h = hs[i];
    if (!h) return hs;
    const n = fn(h);
    return n === h ? hs : hs.map((x, j) => (j === i ? n : x));
  }, clave), [mutar]);
  const deshacer = useCallback(() => {
    const H = hist.current;
    if (!H.pasado.length) return;
    H.futuro.push(hojasRef.current);
    H.clave = null;
    setEditando(false);
    ponerHojas(H.pasado.pop());
  }, [ponerHojas, setEditando]);
  const rehacer = useCallback(() => {
    const H = hist.current;
    if (!H.futuro.length) return;
    H.pasado.push(hojasRef.current);
    H.clave = null;
    setEditando(false);
    ponerHojas(H.futuro.pop());
  }, [ponerHojas, setEditando]);

  /* ── El guardado ──────────────────────────────────────────────────────────
   * Lo que el audit encontró: el autoguardado esperaba 800 ms y, si en ese
   * rato se tocaba «← Mis planillas» (o se cambiaba de pantalla), la limpieza
   * del efecto CANCELABA el guardado y lo último se perdía sin aviso. Y se
   * guardaba a ciegas: si una automatización escribía una celda con la
   * planilla abierta, el próximo autoguardado la pisaba con la copia vieja.
   *
   * Ahora: cada cambio sube `version`; guardar es «llevar a la base la última
   * versión», de a uno por vez (`cola`), así cada guardado lleva el
   * `actualizado_en` que dejó el anterior. Al salir o desmontar se guarda lo
   * pendiente en vez de tirarlo. Si la base dice que la planilla cambió en
   * otro lado, se FRENA el autoguardado y la persona elige: recargar o pisar
   * a conciencia. Todo vive en un ref porque lo leen el desmontaje y el
   * `beforeunload`, que no ven el estado de React. */
  const guardado = useRef({
    version: 0, // sube con cada cambio del dueño
    guardada: 0, // la última versión que llegó a la base
    base: null, // el `actualizado_en` que esta pestaña leyó o guardó por última vez
    timer: null, // el autoguardado esperando sus 800 ms
    cola: Promise.resolve(),
    frenado: false, // hubo conflicto: no se autoguarda hasta que la persona elija
    soltada: false, // se borró o se salió sin guardar: no hay nada que llevar
    lectura: 0, // sube con cada lectura de la base: lo que vuelve de antes se ignora
  });
  const nombreRef = useRef("");
  nombreRef.current = nombre;
  const recienCargado = useRef(null); // lo que vino de la base: eso no es un cambio
  const [carga, setCarga] = useState(0); // «Recargar» la sube y vuelve a leer
  const [quiereSalir, setQuiereSalir] = useState(false); // tocó salir y no se pudo guardar

  /* Pone una planilla recién leída de la base. `enVivo` es la relectura
     silenciosa de cuando otro la guardó y acá no había nada pendiente
     (PlanillaEnVivo): se queda en la misma hoja, y la celda elegida y el
     scroll no se tocan. */
  const ponerLeida = useCallback((p, { enVivo = false } = {}) => {
    const nom = p.nombre || "Sin título";
    const libroLeido = normalizarLibro(p.datos);
    const G = guardado.current;
    clearTimeout(G.timer);
    Object.assign(G, {
      guardada: G.version, base: p.actualizado_en ?? null, timer: null, frenado: false, lectura: G.lectura + 1,
    });
    const hojaVista = enVivo ? Math.max(0, Math.min(activaRef.current, libroLeido.length - 1)) : 0;
    recienCargado.current = { nombre: nom, hojas: libroLeido, activa: hojaVista };
    setNombre(nom);
    hist.current = { pasado: [], futuro: [], clave: null };
    ponerHojas(libroLeido);
    setActiva(hojaVista);
    setMeta({ dueno: p.dueno || "", compartidaCon: p.compartida_con || [], editores: p.editores || [] });
    if (!enVivo) setQuiereSalir(false);
    setEstado("guardado");
  }, [ponerHojas]);

  useEffect(() => {
    let vivo = true;
    traerPlanilla(id)
      .then((p) => { if (vivo) ponerLeida(p); })
      // Cero filas = no existe, está en la papelera o no me la compartieron
      // (p. ej. un link directo que no es para mí): eso tiene su cartel.
      .catch((e) => vivo && setEstado(esSinAcceso(e) ? "sin-acceso" : carga ? "error-recarga" : "error"));
    return () => { vivo = false; };
    // `carga` es el pedido de releer (el botón «Recargar»).
  }, [id, ponerLeida, carga]);

  /* En vivo (FiboSheets #6): si otro la guarda —un editor, el dueño en otra
     pestaña, el bot—, se pone sola si acá no hay nada pendiente, o avisa y
     deja que el guardado optimista choque si lo hay. Ver PlanillaEnVivo. */
  const enVivo = usePlanillaEnVivo({
    id, activo: cargado, guardado, yo,
    alRecargar: (p) => ponerLeida(p, { enVivo: true }),
    alCambiarAcceso: (f) => setMeta((m) => ({
      ...m, compartidaCon: f.compartida_con || m.compartidaCon, editores: f.editores || m.editores,
    })),
  });

  /* Lo que el cálculo necesita del libro: el nombre y las celdas de cada hoja.
     Si nada de eso cambió devuelve EL MISMO arreglo que la vez anterior, así
     cambiar un ancho, un alto o un color —que también renuevan `hojas`— no
     dispara un recálculo de todas las fórmulas. */
  const libroPrevio = useRef([]);
  const libro = useMemo(() => {
    const previo = libroPrevio.current;
    const nuevo = (hojas || []).map((h) => ({ nombre: h.nombre, celdas: h.celdas }));
    const igual = previo.length === nuevo.length
      && nuevo.every((h, i) => h.nombre === previo[i].nombre && h.celdas === previo[i].celdas);
    if (igual) return previo;
    libroPrevio.current = nuevo;
    return nuevo;
  }, [hojas]);

  /* El `hub` de las fórmulas HUB.…: uno por planilla abierta, con su caché.
     Cuando llega un dato sube `hubVersion` y eso recalcula la hoja (varios que
     llegan juntos son un solo recálculo). Cada TTL_MS, si alguna fórmula lo
     usa, se recalcula igual: lo vencido se vuelve a pedir y la planilla
     abierta se mantiene al día sola. */
  const [hubVersion, setHubVersion] = useState(0);
  const hubRef = useRef(null);
  if (!hubRef.current) {
    hubRef.current = crearHub({ datos: datosDelHub, alLlegar: () => setHubVersion((v) => v + 1) });
  }
  useEffect(() => {
    const hub = hubRef.current;
    hub.abrir();
    const t = setInterval(() => { if (hub.usado()) setHubVersion((v) => v + 1); }, TTL_MS);
    return () => { clearInterval(t); hub.cerrar(); };
  }, []);

  /* Se calcula con el LIBRO entero (no sólo la hoja a la vista) para que las
     fórmulas entre hojas (=SUMA(Enero!A1:A10)) resuelvan. Depende de todas las
     hojas: si cambia otra, lo que la referencia se recalcula. `motivos` es el
     porqué de cada error que lo trae (los de HUB.…), para el mouse. */
  const { valores, motivos } = useMemo(() => {
    const h = libro[activa] || libro[0];
    const m = new Map();
    const v = h ? evaluar(h.celdas, { hojas: libro, hojaActual: h.nombre, hub: hubRef.current, motivos: m }) : [];
    return { valores: v, motivos: m };
    // `hubVersion` no se lee adentro: es el aviso de que el caché del hub cambió.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libro, activa, hubVersion]);

  /* Formato condicional y listas desplegables: una pasada por render sobre los
     valores ya calculados (ver `shared/formatoCondicional`). */
  const condicional = hoja ? hoja.condicional : null;
  const validacion = hoja ? hoja.validacion : null;
  const reglasCalc = useMemo(
    () => calcularFormato({ condicional, validacion, libro, activa, valores, hub: hubRef.current }),
    [condicional, validacion, libro, activa, valores],
  );

  /* Un guardado: lleva la última versión, si hay algo que llevar. Devuelve si
     quedó todo en la base. Con el tope de la base el cartel se queda hasta que un
     guardado entre (no parpadea con «Guardando…» en cada reintento). */
  const unGuardado = useCallback(async ({ pisar = false } = {}) => {
    const G = guardado.current;
    if (G.soltada) return true;
    if (G.frenado && !pisar) return false;
    if (G.version === G.guardada && !pisar) return true;
    const v = G.version;
    const lectura = G.lectura;
    setEstado((e) => (e === "grande" ? e : "guardando"));
    try {
      const nueva = await guardarPlanilla(
        id, nombreRef.current, empaquetarLibro(hojasRef.current, activaRef.current), G.base, { pisar },
      );
      if (G.lectura !== lectura) return false; // se recargó mientras viajaba: manda lo leído
      if (nueva) { G.base = nueva; anotarPropio(G, nueva); } // anotado: el eco del realtime no es «otro»
      G.guardada = v;
      G.frenado = false;
      // Si siguió escribiendo mientras viajaba, ya hay otro guardado en camino.
      setEstado(G.version === v ? "guardado" : "guardando");
      return true;
    } catch (e) {
      if (G.lectura !== lectura) return false;
      if (e && e.codigo === CONFLICTO) { G.frenado = true; setEstado("conflicto"); }
      else if (e && e.codigo === MUY_GRANDE) setEstado("grande");
      else setEstado("error");
      return false;
    }
  }, [id]);
  const guardarYa = useCallback((opciones) => {
    const G = guardado.current;
    clearTimeout(G.timer);
    G.timer = null;
    const corrida = G.cola.then(() => unGuardado(opciones));
    G.cola = corrida.catch(() => {});
    return corrida;
  }, [unGuardado]);

  /* Autoguardado: sólo el dueño escribe. En una planilla compartida (solo
     lectura) esto no corre. Guarda el libro entero y qué hoja quedó activa.
     El efecto NO cancela el temporizador al limpiarse: lo pendiente se guarda
     igual al salir (ver el desmontaje, abajo). */
  useEffect(() => {
    if (!cargado || !esMio) return;
    const r = recienCargado.current;
    if (r) {
      if (r.nombre === nombre && r.hojas === hojas && r.activa === activa) return;
      recienCargado.current = null;
    }
    const G = guardado.current;
    G.version += 1;
    if (G.frenado || G.soltada) return; // en conflicto, la persona elige
    setEstado((e) => (e === "grande" ? e : "guardando"));
    clearTimeout(G.timer);
    G.timer = setTimeout(() => { G.timer = null; guardarYa(); }, 800);
  }, [nombre, hojas, activa, cargado, esMio, guardarYa]);

  /* Al desmontar (cambiar de pantalla en el hub) se guarda lo pendiente, no se
     tira. Y si la pestaña se esconde (cambiar de pestaña, cerrar en el
     celular) se adelanta el guardado: puede ser lo último que se alcance. */
  useEffect(() => {
    const alEsconder = () => { if (document.visibilityState === "hidden") guardarYa(); };
    document.addEventListener("visibilitychange", alEsconder);
    return () => {
      document.removeEventListener("visibilitychange", alEsconder);
      guardarYa();
    };
  }, [guardarYa]);

  /* Cerrar la pestaña con algo sin guardar (esperando, viajando o rebotado)
     pide confirmación. Con todo guardado, no molesta. */
  useEffect(() => {
    const G = guardado.current; // el mismo objeto toda la vida de la pantalla
    const avisar = (e) => {
      if (G.soltada || G.version === G.guardada) return undefined;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", avisar);
    /* AL SALIR DE LA PANTALLA EL AVISO SE QUEDA HASTA QUE LLEGUE LO PENDIENTE.
       El desmontaje de arriba manda el libro entero sin esperar, y eso viaja
       un rato (varios MB con una línea mala). Mientras viaja, cerrar la
       pestaña o recargarla lo corta. Desde el 09/10 el hub se recarga solo
       al entrar a CRT o MIC si hay versión nueva y nada a medio cargar
       (Fabián e Igna): si se va de acá directo para allá, esto es lo que le
       dice «todavía no». Se saca cuando la cola de guardados termina, bien o
       mal; se mira en una microtarea para que el guardado del desmontaje ya
       esté en la cola, sea cual sea el orden de las limpiezas. */
    return () => {
      Promise.resolve()
        .then(() => G.cola)
        .finally(() => window.removeEventListener("beforeunload", avisar));
    };
  }, []);

  /* «← Mis planillas»: primero se guarda lo pendiente; si no se pudo, se queda
     acá con el cartel que dice por qué (y ahí está «Salir sin guardar»). */
  const salir = async () => {
    if (!esMio) { onSalir(); return; }
    const G = guardado.current;
    let ok = await guardarYa();
    while (ok && !G.soltada && G.version !== G.guardada) ok = await guardarYa();
    if (ok) onSalir();
    else setQuiereSalir(true);
  };
  const salirSinGuardar = () => {
    const G = guardado.current;
    clearTimeout(G.timer);
    G.soltada = true;
    onSalir();
  };
  const recargar = () => {
    setEstado("cargando");
    setCarga((c) => c + 1);
  };
  /* Para el historial: antes de restaurar una versión se guarda lo pendiente
     (si no, la restaurada chocaría con el propio autoguardado), y se le pasa
     la versión que esta pestaña tiene en la mano para el guardado
     condicional. null = no se pudo guardar: el cartel de arriba dice por qué. */
  const listoParaRestaurar = async () => {
    const G = guardado.current;
    let ok = await guardarYa();
    while (ok && !G.soltada && G.version !== G.guardada) ok = await guardarYa();
    return ok ? { nombre: nombreRef.current, actualizadoEn: G.base } : null;
  };

  /* La campanita: mis avisos prendidos sobre esta planilla. Si no se pueden
     traer, no hay campanitas y nada más: la planilla tiene que abrir igual. */
  useEffect(() => {
    if (!cargado || !esDueno || !yo) return undefined;
    let vivo = true;
    Promise.resolve()
      .then(() => misAvisosDePlanilla(id, yo))
      .then((as) => { if (vivo) setAvisos(Array.isArray(as) ? as : []); })
      .catch(() => { if (vivo) setAvisos([]); });
    return () => { vivo = false; };
  }, [id, yo, esDueno, cargado]);
  const vigilados = useMemo(
    () => (hoja ? rangosVigilados(avisos, id, hoja.nombre, activa) : []),
    [avisos, id, hoja, activa],
  );

  const setAnchosActiva = useCallback((fn) => mutarHoja((h) => ({ ...h, anchos: fn(h.anchos || {}) })), [mutarHoja]);
  const setAltosActiva = useCallback((fn) => mutarHoja((h) => ({ ...h, altos: fn(h.altos || {}) })), [mutarHoja]);

  /* Al escribir, la planilla CRECE sola: si cargás algo en la última fila o la
     última columna, se agrega una más para que siempre haya lugar por delante
     (lo hace `escribir`). */
  const setCelda = useCallback((r, c, v, clave = null) => {
    mutarHoja((h) => ((h.celdas[r] && h.celdas[r][c] === v) ? h : escribir(h, [{ r, c, v }])), clave);
  }, [mutarHoja]);

  /* Lo que se armó en el último render y los manejadores necesitan: qué filas
     están a la vista (por los filtros), cuántas columnas hay, los valores. */
  const geo = useRef({ filasNav: [], cuerpo: [], cols: 0, filas: 0, offCuerpo: null, RH: 30, valores: [] });

  /* Lo elegido. Arrastrando o con Mayús+clic abarca enteras las combinadas que
     toca, como en Excel (ver `rangoElegido`): es lo que se ve marcado, y lo
     que copian, cortan, borran o formatean las acciones. */
  const combActivas = () => {
    const h = hojasRef.current && hojasRef.current[activaRef.current];
    return h ? h.combinadas : null;
  };
  const rangoSel = () => (selRef.current ? rangoElegido(selRef.current, sel2Ref.current, combActivas()) : null);
  const crudo = (r, c) => {
    const h = hojasRef.current && hojasRef.current[activaRef.current];
    return h && h.celdas[r] && h.celdas[r][c] != null ? h.celdas[r][c] : "";
  };
  /* Desplaza la grilla hasta una fila que no está dibujada. */
  const llevarScrollA = (r) => {
    const g = geo.current;
    const i = g.cuerpo.indexOf(r);
    const wrap = wrapRef.current;
    if (i < 0 || !wrap) return;
    const top = Math.max(0, (g.offCuerpo ? g.offCuerpo[i] : i * g.RH) - g.RH * 2);
    // `top` está en px de la hoja; el scroll, en px de pantalla (con zoom).
    wrap.scrollTop = top * zoomRef.current;
    setScroll((s) => ({ ...s, top: top * zoomRef.current }));
  };

  /* Mover la celda activa. Si su `<input>` está dibujado se enfoca en el acto;
     si no (está más allá de lo que se ve), se desplaza la grilla y el foco se
     pone cuando aparece. Con `quieto` no se desplaza nada: es para cuando se
     elige una columna entera estando lejos de la fila 1 — el foco queda en la
     grilla, que alcanza para copiar, borrar y dar formato. */
  const focoPendiente = useRef(null);
  const irA = (r, c, s2 = null, opciones = {}) => {
    // Una celda tapada por una combinada lleva a su ancla, como en Excel.
    const ancla = s2 ? null : anclaDe(geo.current.comb, r, c);
    if (ancla) { r = ancla.r; c = ancla.c; }
    setEditando(false);
    elegir({ r, c }, s2);
    const el = inputEn(tablaRef.current, r, c);
    if (el) {
      if (document.activeElement !== el) el.focus({ preventScroll: !!opciones.quieto });
      if (!opciones.quieto) traerALaVista(el);
    } else if (opciones.quieto) {
      if (wrapRef.current) wrapRef.current.focus({ preventScroll: true });
    } else {
      focoPendiente.current = { r, c };
      llevarScrollA(r);
    }
  };
  useEffect(() => {
    const p = focoPendiente.current;
    if (!p) return;
    const el = inputEn(tablaRef.current, p.r, p.c);
    if (!el) return;
    focoPendiente.current = null;
    el.focus();
    traerALaVista(el);
  });

  /* Lleva la otra esquina del rango a (r, c); la celda activa se queda. Con
     `conMouse` (Mayús+clic) lo elegido abarca las combinadas que toca. */
  const extenderA = (r, c, conMouse = false) => {
    const s = selRef.current;
    setEditando(false);
    elegir(s, r === s.r && c === s.c ? null : { r, c, ...(conMouse ? { comb: true } : {}) });
    const el = inputEn(tablaRef.current, r, c);
    if (el) traerALaVista(el); else llevarScrollA(r);
  };

  /* La esquina de lo elegido opuesta a la celda activa, la que mueve
     Mayús+flecha: la del rango que se ve (si el mouse abarcó una combinada,
     la de la combinada, no la celda que se tocó). Si esa fila la esconde un
     filtro, la que se tocó: se camina por las filas que se ven. */
  const otraEsquina = () => {
    const s = selRef.current;
    const o = sel2Ref.current || s;
    const rg = rangoSel();
    const r = o.r >= s.r ? rg.r1 : rg.r0;
    const nav = geo.current.filasNav;
    return { r: !nav.length || nav.includes(r) ? r : o.r, c: o.c >= s.c ? rg.c1 : rg.c0 };
  };

  /* Un paso (o varios) en una dirección, salteando las filas que el filtro
     esconde. Con `extender` (Shift) la celda activa se queda y se mueve la otra
     esquina del rango. */
  const mover = (dr, dc, extender = false) => {
    const s = selRef.current;
    if (!s) return;
    const g = geo.current;
    const base = extender ? otraEsquina() : bordeAlMover(g.comb, s, dr, dc);
    const i = Math.max(0, g.filasNav.indexOf(base.r));
    const r = g.filasNav[Math.max(0, Math.min(g.filasNav.length - 1, i + dr))] ?? base.r;
    const c = Math.max(0, Math.min(g.cols - 1, base.c + dc));
    if (extender) extenderA(r, c); else irA(r, c);
  };

  /* Ctrl+flecha: hasta el borde del bloque de datos, como en Excel. */
  const saltar = (dr, dc, extender) => {
    const s = selRef.current;
    if (!s) return;
    const g = geo.current;
    const base = extender ? otraEsquina() : s;
    const lleno = (r, c) => crudo(r, c) !== "";
    const dentro = (r, c) => r >= 0 && c >= 0 && r < g.filas && c < g.cols;
    let { r, c } = base;
    if (dentro(r + dr, c + dc) && lleno(r, c) && lleno(r + dr, c + dc)) {
      while (dentro(r + dr, c + dc) && lleno(r + dr, c + dc)) { r += dr; c += dc; }
    } else {
      do { r += dr; c += dc; } while (dentro(r + dr, c + dc) && !lleno(r, c));
      if (!dentro(r, c)) { r -= dr; c -= dc; }
    }
    if (extender) extenderA(r, c); else irA(r, c);
  };

  /* ── Editar una celda ─────────────────────────────────────────────────── */

  const sesion = useRef(0); // cada edición de una celda es UN paso de deshacer
  const cursorAlFinal = useRef(false);
  const empezarEdicion = () => {
    if (!esMioRef.current || !selRef.current) return;
    sesion.current += 1;
    cursorAlFinal.current = true;
    setEditando(true);
  };
  useEffect(() => {
    if (!editando || !cursorAlFinal.current || !sel) return;
    cursorAlFinal.current = false;
    const el = inputEn(tablaRef.current, sel.r, sel.c);
    if (!el) return;
    if (document.activeElement !== el) el.focus();
    try { el.setSelectionRange(el.value.length, el.value.length); } catch { /* no es texto seleccionable */ }
  }, [editando, sel]);
  /* Escape: la celda vuelve a lo que tenía antes de empezar a escribir. */
  const cancelarEdicion = () => {
    const H = hist.current;
    if (H.clave === `celda:${sesion.current}` && H.pasado.length) {
      H.clave = null;
      ponerHojas(H.pasado.pop());
    }
    setEditando(false);
  };

  /* ── Formato ──────────────────────────────────────────────────────────── */

  /* Aplica un cambio de formato a todo lo seleccionado; si una celda queda sin
     nada, se saca su clave. */
  const aplicarFormato = (cambio) => {
    const rg = rangoSel();
    if (!rg) return;
    mutarHoja((h) => {
      const out = { ...(h.formato || {}) };
      const r1 = Math.min(rg.r1, h.celdas.length - 1);
      for (let r = rg.r0; r <= r1; r += 1) {
        for (let c = rg.c0; c <= rg.c1; c += 1) {
          const ref = refDe(r, c);
          const nuevo = { ...(out[ref] || {}), ...cambio };
          Object.keys(nuevo).forEach((k) => nuevo[k] === undefined && delete nuevo[k]);
          if (Object.keys(nuevo).length) out[ref] = nuevo; else delete out[ref];
        }
      }
      return { ...h, formato: out };
    });
  };
  const bordesSel = (tipo) => {
    const rg = rangoSel();
    if (rg) mutarHoja((h) => ({ ...h, formato: aplicarBordes(h.formato, { ...rg, r1: Math.min(rg.r1, h.celdas.length - 1) }, tipo) }));
  };
  /* Ctrl+\: lo seleccionado vuelve a como viene una celda nueva. */
  const borrarFormatoSel = () => {
    const rg = rangoSel();
    if (!rg) return;
    mutarHoja((h) => {
      const out = { ...(h.formato || {}) };
      for (let r = rg.r0; r <= Math.min(rg.r1, h.celdas.length - 1); r += 1) {
        for (let c = rg.c0; c <= rg.c1; c += 1) {
          const ref = refDe(r, c);
          // La nota no es formato: se queda.
          if (out[ref] && out[ref].nota) out[ref] = { nota: out[ref].nota }; else delete out[ref];
        }
      }
      return { ...h, formato: out };
    });
  };
  /* La brocha: toma el formato de lo seleccionado; lo próximo que se elija
     (una celda o un rango, con el mouse) queda con ese formato. */
  const tomarBrocha = () => {
    if (brochaRef.current) { setBrocha(null); return; }
    const rg = rangoSel();
    const h = hojasRef.current && hojasRef.current[activaRef.current];
    if (!rg || !h) return;
    const tope = { ...rg, r1: Math.min(rg.r1, h.celdas.length - 1), c1: Math.min(rg.c1, geo.current.cols - 1) };
    setBrocha(copiarRango(h, tope).formatos);
  };
  const colores = (juego) => mutarHoja((h) => {
    const { alternos: _antes, ...resto } = h;
    return juego === "ninguno" ? resto : { ...resto, alternos: juego };
  });
  const abrirNota = () => {
    const s = selRef.current;
    const h = hojasRef.current && hojasRef.current[activaRef.current];
    if (!s || !h) return;
    const f = h.formato && h.formato[refDe(s.r, s.c)];
    setNotaEdit({ r: s.r, c: s.c, texto: (f && f.nota) || "" });
  };
  const guardarNota = (texto) => {
    if (!notaEdit) return;
    const { r, c } = notaEdit;
    const limpio = String(texto || "").trim();
    mutarHoja((h) => {
      const out = { ...(h.formato || {}) };
      const ref = refDe(r, c);
      const nuevo = { ...(out[ref] || {}) };
      if (limpio) nuevo.nota = limpio; else delete nuevo.nota;
      if (Object.keys(nuevo).length) out[ref] = nuevo; else delete out[ref];
      return { ...h, formato: out };
    });
    setNotaEdit(null);
    irA(r, c);
  };
  /* Σ: la fórmula va abajo de lo elegido, o en la celda sumando lo de arriba. */
  const sumaRapida = (fn) => {
    const rg = rangoSel();
    if (!rg) return;
    const { cambios, foco } = autoSuma(geo.current.valores, rg, fn);
    mutarHoja((h) => escribir(h, cambios));
    irA(foco.r, foco.c);
  };
  /* «Combinar y centrar» (Lautaro, 19/10/2026: «COMBINAR Y CENTRAR COMO EN
     EXCEL»). Lo que decide qué hacer está en `shared/planillaCombinadas`: acá
     sólo se pregunta antes de perder algo —en la página, no con un cartel del
     navegador— y queda elegida la celda que resultó. Es un cambio más:
     Ctrl+Z lo deshace, y se guarda con el resto (y el historial lo tiene).
     La pregunta es de la hoja tal como estaba: cualquier cambio la cierra
     (`ponerHojas`, `limpiarVista`), y «Combinar igual» vuelve a mirar. */
  const hacerCombinar = (rg) => {
    setPedidoComb(null);
    mutarHoja((h) => combinarYCentrar(h, rg));
    irA(rg.r0, rg.c0);
  };
  const combinarSel = () => {
    const rg = rangoSel();
    const h = hojasRef.current && hojasRef.current[activaRef.current];
    if (!rg || !h || !esMioRef.current) return;
    const tope = { ...rg, r1: Math.min(rg.r1, h.celdas.length - 1), c1: Math.min(rg.c1, (h.celdas[0] || []).length - 1) };
    const plan = queHaceCombinar(h.combinadas, tope);
    setPedidoComb(null);
    if (plan.accion === "cruce") { setAvisoPropio(textoCruce(tope, plan.con)); return; }
    if (plan.accion === "separar") {
      const sep = plan.rango;
      mutarHoja((x) => separarCombinada(x, sep));
      // Queda elegido lo que era la combinada, como en Excel.
      irA(sep.r0, sep.c0, { r: sep.r1, c: sep.c1 });
      return;
    }
    if (plan.accion !== "combinar") return;
    const seBorran = seBorranAlCombinar(h, tope);
    if (seBorran.length) setPedidoComb({ rango: tope, hoja: h, seBorran });
    else hacerCombinar(tope);
  };
  /* «Cancelar» (o Escape): no se toca nada y el teclado vuelve a la grilla. */
  const noCombinar = () => {
    setPedidoComb(null);
    const s = selRef.current;
    if (s) irA(s.r, s.c, sel2Ref.current);
  };
  /* «Combinar igual»: se combina sólo si la hoja a la vista es la de la
     pregunta, sin ningún cambio. Si es la misma pero cambió, se pregunta de
     nuevo con lo que se perdería ahora; si es otra, no se toca nada. Ver
     `revisarAntesDeCombinar`. */
  const combinarIgual = () => {
    const h = hojasRef.current && hojasRef.current[activaRef.current];
    const plan = esMioRef.current ? revisarAntesDeCombinar(pedidoComb, h) : { accion: "nada" };
    if (plan.accion === "combinar") { hacerCombinar(plan.rango); return; }
    if (plan.accion === "preguntar") { setPedidoComb({ rango: plan.rango, hoja: h, seBorran: plan.seBorran }); return; }
    noCombinar();
    if (plan.accion === "cruce") setAvisoPropio(textoCruce(plan.rango, plan.con));
  };
  const alternarFormato = (clave) => {
    const s = selRef.current;
    const h = hojasRef.current && hojasRef.current[activaRef.current];
    if (!s || !h) return;
    const f = h.formato && h.formato[refDe(s.r, s.c)];
    aplicarFormato({ [clave]: f && f[clave] ? undefined : true });
  };

  /* ── Portapapeles ─────────────────────────────────────────────────────────
   * Al sistema va el texto con tabuladores (lo que entiende Excel o la misma
   * planilla), con los valores A LA VISTA, y además la tabla con su formato
   * (`text/html`), que un correo pega como tabla con bordes (ticket 464, ver
   * `shared/planillaCopiar`). Aparte se guarda el bloque crudo —fórmulas y
   * formatos— para cuando se pega acá adentro: si lo que llega al pegar es el
   * mismo texto que salió, se usa eso.
   * Se copia lo elegido, lo que se ve marcado, ni más ni menos: que el bitren
   * entre entero arrastrando hasta su esquina de abajo lo hace la selección
   * (`rangoSel`), no el copiar. */
  const portapapeles = useRef(null);
  /* Lo elegido que se copia, dentro de la hoja. */
  const rangoACopiar = () => {
    const rg = rangoSel();
    const h = hojasRef.current && hojasRef.current[activaRef.current];
    if (!rg || !h) return null;
    return { h, tope: { ...rg, r1: Math.min(rg.r1, h.celdas.length - 1), c1: Math.min(rg.c1, geo.current.cols - 1) } };
  };
  const copiarSeleccion = (cortar) => {
    const a = rangoACopiar();
    if (!a) return null;
    const { h, tope } = a;
    const { crudos, formatos, reglas } = copiarRango(h, tope);
    const vals = geo.current.valores;
    const texto = aTSV(crudos.map((fila, i) => fila.map((_, j) => {
      const v = vals[tope.r0 + i] ? vals[tope.r0 + i][tope.c0 + j] : "";
      return formatear(v);
    })));
    // Una celda (o una combinada) sola va como texto, como siempre: pegada en
    // un correo o en un formulario es un dato, no una tabla de una celda con
    // borde. Ver `tablaParaCopiar`.
    const html = tablaParaCopiar(h, vals, tope, { estilos: geo.current.estilos });
    portapapeles.current = { texto, crudos, formatos, reglas, origen: tope, hoja: h.nombre, cortar: !!cortar && esMioRef.current };
    return { texto, html };
  };
  /* «Copiar como imagen» (para WhatsApp, que no muestra tablas): lo mismo que
     se copia, dibujado en un PNG, al portapapeles. Si el navegador no deja
     escribir imágenes, lo dice y la ofrece para bajar. Nunca un `alert()`. */
  const soltarImagen = () => setAvisoImagen(null);
  // La imagen ofrecida para bajar se suelta cuando el aviso se va (o cambia).
  useEffect(() => () => { if (avisoImagen && avisoImagen.url) URL.revokeObjectURL(avisoImagen.url); }, [avisoImagen]);
  const copiarComoImagen = () => {
    const a = rangoACopiar();
    if (!a) return;
    const { h, tope } = a;
    soltarImagen();
    if (!hayLienzo()) { setAvisoPropio(IMAGEN_SIN_LIENZO); return; }
    const lienzo = dibujarSeleccion(seleccionConFormato(h, geo.current.valores, tope, { estilos: geo.current.estilos }));
    if (!lienzo) { setAvisoPropio(IMAGEN_MUY_GRANDE); return; }
    const nombre = nombreDeImagen(nombreRef.current, h.nombre, tope);
    copiarImagen(lienzo).then(({ copiada, png }) => {
      if (copiada) { setAvisoPropio(IMAGEN_COPIADA); return; }
      png.then(
        (blob) => setAvisoImagen({ texto: IMAGEN_NO_SE_COPIA, url: URL.createObjectURL(blob), nombre }),
        () => setAvisoPropio(IMAGEN_FALLO),
      );
    });
  };
  /* Con `soloValores` (Ctrl+Mayús+V) entra lo que se VE: ni fórmulas ni
     formatos, aunque lo copiado sea de esta misma planilla. */
  const pegarTexto = (texto, opciones = {}) => {
    const rg = rangoSel();
    if (!rg || !esMioRef.current) return;
    const pp = portapapeles.current;
    const interno = !opciones.soloValores && !!pp && sinFinDeLinea(pp.texto) === sinFinDeLinea(texto);
    const bloque = interno ? pp.crudos : deTSV(texto);
    const mueve = interno && pp.cortar;
    const elegido = rg.r0 !== rg.r1 || rg.c0 !== rg.c1;
    mutar((hs) => hs.map((h, i) => {
      let n = h;
      if (mueve && h.nombre === pp.hoja) n = borrarRango(n, pp.origen);
      if (i === activaRef.current) {
        n = pegar(n, { r: rg.r0, c: rg.c0 }, bloque, {
          origen: interno ? pp.origen : null,
          formatos: interno ? pp.formatos : null,
          reglas: interno ? pp.reglas : null,
          cortar: mueve,
          hasta: elegido && !mueve ? rg : null,
        });
      }
      return n;
    }));
    if (mueve) portapapeles.current = { ...pp, cortar: false };
    // Queda seleccionado lo pegado.
    const lado = (pedido, propio) => (elegido && !mueve && pedido > propio && pedido % propio === 0 ? pedido : propio);
    const alto = lado(rg.r1 - rg.r0 + 1, bloque.length);
    const ancho = lado(rg.c1 - rg.c0 + 1, bloque[0].length);
    elegir({ r: rg.r0, c: rg.c0 }, alto > 1 || ancho > 1 ? { r: rg.r0 + alto - 1, c: rg.c0 + ancho - 1 } : null);
  };
  const enGrilla = (t) => esCeldaDeGrilla(t) || t === wrapRef.current;
  const alCopiar = (e, cortar) => {
    if (editRef.current || !enGrilla(e.target)) return; // editando: copia el texto elegido, lo normal
    const copia = copiarSeleccion(cortar);
    if (!copia) return;
    e.clipboardData.setData("text/plain", copia.texto);
    if (copia.html) e.clipboardData.setData("text/html", copia.html);
    e.preventDefault();
  };
  const alPegar = (e) => {
    if (editRef.current || !enGrilla(e.target) || !esMioRef.current) return;
    e.preventDefault();
    pegarTexto(e.clipboardData.getData("text/plain"));
  };

  const borrarSeleccion = () => {
    const rg = rangoSel();
    if (rg) mutarHoja((h) => borrarRango(h, rg));
  };

  /* Ctrl+D: copia hacia abajo. Con varias filas elegidas, la primera llena las
     demás; con una sola, trae lo de la fila de arriba. */
  const rellenarAbajo = () => {
    const rg = rangoSel();
    if (!rg) return;
    if (rg.r0 !== rg.r1) mutarHoja((h) => rellenar(h, { ...rg, r1: rg.r0 }, rg, { serie: false }));
    else if (rg.r0 > 0) mutarHoja((h) => rellenar(h, { ...rg, r0: rg.r0 - 1, r1: rg.r0 - 1 }, { ...rg, r0: rg.r0 - 1 }, { serie: false }));
  };

  const estructura = (eje, desde, cuantas) => {
    mutar((hs) => cambiarEstructura(hs, activaRef.current, eje, desde, cuantas));
  };
  const ordenarPor = (ascendente) => {
    const s = selRef.current;
    if (!s) return;
    mutarHoja((h) => ordenar(h, s.c, ascendente, geo.current.valores));
  };
  const alternarFija = (cual) => mutarHoja((h) => ({ ...h, fija: { ...(h.fija || {}), [cual]: !(h.fija && h.fija[cual]) } }));

  const buscarSiguiente = () => {
    const p = buscar(geo.current.valores, busca, selRef.current);
    setSinResultado(!p && !!busca.trim());
    if (!p) return;
    irA(p.r, p.c);
    // El foco vuelve al buscador, así otro Enter sigue con la próxima.
    window.requestAnimationFrame(() => buscaRef.current && buscaRef.current.focus());
  };

  /* ── Seleccionar con el mouse ─────────────────────────────────────────── */

  const arrastrandoSel = useRef(false);
  useEffect(() => {
    // Con la brocha cargada, al terminar de elegir dónde, pinta.
    const soltar = () => {
      if (arrastrandoSel.current && brochaRef.current) api.current.pintarConBrocha();
      arrastrandoSel.current = false;
    };
    window.addEventListener("mouseup", soltar);
    return () => window.removeEventListener("mouseup", soltar);
  }, []);

  const elegirColumna = (c, e) => {
    if (e.button) return;
    e.preventDefault();
    const s = selRef.current;
    irA(0, e.shiftKey && s ? s.c : c, { r: geo.current.filas - 1, c }, { quieto: true });
  };
  const elegirFila = (r, e) => {
    if (e.button) return;
    e.preventDefault();
    const s = selRef.current;
    irA(e.shiftKey && s ? s.r : r, 0, { r, c: geo.current.cols - 1 }, { quieto: true });
  };
  const elegirTodo = () => {
    const g = geo.current;
    irA(0, 0, { r: g.filas - 1, c: g.cols - 1 }, { quieto: true });
  };

  /* El menú del botón derecho. Si se abre sobre algo que no está seleccionado,
     primero se lo selecciona (como hace Excel). */
  const abrirMenu = (e, tipo, i) => {
    if (editRef.current) return; // editando, el menú del navegador (pegar texto, corrector)
    e.preventDefault();
    const rg = rangoSel();
    const g = geo.current;
    if (tipo === "col" && !(rg && rg.r0 === 0 && rg.r1 === g.filas - 1 && i >= rg.c0 && i <= rg.c1)) {
      irA(0, i, { r: g.filas - 1, c: i }, { quieto: true });
    } else if (tipo === "fila" && !(rg && rg.c0 === 0 && rg.c1 === g.cols - 1 && i >= rg.r0 && i <= rg.r1)) {
      irA(i, 0, { r: i, c: g.cols - 1 }, { quieto: true });
    }
    setMenu({ x: e.clientX, y: e.clientY, tipo });
  };
  useEffect(() => {
    if (!menu) return undefined;
    const cerrar = () => setMenu(null);
    window.addEventListener("mousedown", cerrar);
    window.addEventListener("scroll", cerrar, true);
    window.addEventListener("resize", cerrar);
    return () => {
      window.removeEventListener("mousedown", cerrar);
      window.removeEventListener("scroll", cerrar, true);
      window.removeEventListener("resize", cerrar);
    };
  }, [menu]);

  /* ── Las acciones de una celda ────────────────────────────────────────────
   * `api` tiene siempre las de ESTE render; `acc` es un objeto que no cambia
   * nunca y las llama. Así el memo de la celda no se rompe y ninguna acción
   * trabaja con estado viejo. */
  const api = useRef({});
  api.current = {
    pintarConBrocha: () => {
      const patron = brochaRef.current;
      const rg = rangoSel();
      setBrocha(null);
      if (!patron || !rg) return;
      mutarHoja((h) => ({ ...h, formato: pegarFormato(h.formato, patron, { ...rg, r1: Math.min(rg.r1, h.celdas.length - 1) }) }));
    },
    foco: (r, c) => {
      const s = selRef.current;
      if (s && s.r === r && s.c === c) return;
      setEditando(false);
      elegir({ r, c }, null);
    },
    cambio: (r, c, v) => {
      if (!esMioRef.current) return;
      const s = selRef.current;
      const misma = !!s && s.r === r && s.c === c;
      if (!misma) elegir({ r, c }, null);
      if (!misma || !editRef.current) { sesion.current += 1; setEditando(true); }
      setCelda(r, c, v, `celda:${sesion.current}`);
    },
    mouseDown: (r, c, e) => {
      const s = selRef.current;
      if (e.button === 2) { // botón derecho: sobre lo ya elegido no cambia la selección
        const rg = rangoSel();
        if (!rg || r < rg.r0 || r > rg.r1 || c < rg.c0 || c > rg.c1) irA(r, c);
        return;
      }
      if (e.button) return;
      if (e.shiftKey && s) {
        e.preventDefault();
        extenderA(r, c, true);
        return;
      }
      // En la celda que se está editando, el clic pone el cursor: lo normal.
      if (editRef.current && s && s.r === r && s.c === c) return;
      e.preventDefault(); // sin esto el navegador selecciona texto al arrastrar
      arrastrandoSel.current = true;
      irA(r, c);
    },
    mouseEnter: (r, c) => {
      const s = selRef.current;
      if (!arrastrandoSel.current || !s) return;
      elegir(s, r === s.r && c === s.c ? null : { r, c, comb: true });
    },
    editar: (r, c) => {
      if (!esMioRef.current) return;
      const s = selRef.current;
      if (!s || s.r !== r || s.c !== c) irA(r, c);
      if (!editRef.current) empezarEdicion();
    },
    /* Arrastrar el cuadradito de la esquina rellena hacia donde se arrastre. */
    asaDown: (e) => {
      if (e.button || !esMioRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const origen = rangoSel();
      if (!origen) return;
      let destino = null;
      const alMover = (ev) => {
        const el = document.elementFromPoint ? document.elementFromPoint(ev.clientX, ev.clientY) : null;
        const td = el && el.closest ? el.closest("td[data-r]") : null;
        if (!td) return;
        const r = Number(td.dataset.r);
        const c = Number(td.dataset.c);
        if (r > origen.r1) destino = { ...origen, r1: r };
        else if (r < origen.r0) destino = { ...origen, r0: r };
        else if (c > origen.c1) destino = { ...origen, c1: c };
        else if (c < origen.c0) destino = { ...origen, c0: c };
        else destino = null;
        setRelleno(destino);
      };
      const alSoltar = (ev) => {
        window.removeEventListener("pointermove", alMover);
        window.removeEventListener("pointerup", alSoltar);
        window.removeEventListener("pointercancel", alSoltar);
        document.body.classList.remove("esp-rellenando");
        setRelleno(null);
        if (ev.type === "pointercancel" || !destino) return;
        const d = destino;
        mutarHoja((h) => rellenar(h, origen, d));
        irA(d.r0, d.c0, { r: d.r1, c: d.c1 }, { quieto: true });
      };
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* sin captura igual anda: escucha `window` */ }
      window.addEventListener("pointermove", alMover);
      window.addEventListener("pointerup", alSoltar);
      window.addEventListener("pointercancel", alSoltar);
      document.body.classList.add("esp-rellenando");
    },
    /* La flechita de una celda con lista desplegable: elige la celda y abre
       la lista abajo de ella. */
    abrirLista: (r, c, el) => {
      if (!esMioRef.current) return;
      setEditando(false);
      elegir({ r, c }, null);
      const td = el && el.closest ? el.closest("td") : null;
      const b = td && td.getBoundingClientRect ? td.getBoundingClientRect() : { left: 0, bottom: 0, width: 0 };
      setListaAbierta({ r, c, x: b.left, y: b.bottom, ancho: b.width });
    },
  };
  const acc = useMemo(() => ({
    foco: (...a) => api.current.foco(...a),
    cambio: (...a) => api.current.cambio(...a),
    mouseDown: (...a) => api.current.mouseDown(...a),
    mouseEnter: (...a) => api.current.mouseEnter(...a),
    editar: (...a) => api.current.editar(...a),
    asaDown: (...a) => api.current.asaDown(...a),
    abrirLista: (...a) => api.current.abrirLista(...a),
  }), []);

  /* ── El teclado ───────────────────────────────────────────────────────── */
  const alTeclear = (e) => {
    if (!enGrilla(e.target)) return;
    const s = selRef.current;
    if (!s) return;
    const t = e.target;
    const k = e.key;
    const ctrl = e.ctrlKey || e.metaKey;
    const letra = k.length === 1 ? k.toLowerCase() : "";
    const hacer = (fn) => { e.preventDefault(); fn(); };

    if (ctrl && letra === "z") { hacer(e.shiftKey ? rehacer : deshacer); return; }
    if (ctrl && letra === "y") { hacer(rehacer); return; }

    if (editRef.current) {
      // Enter/↑/↓ saltan de fila siempre; ←/→ sólo cuando el cursor está en el
      // borde del texto, así adentro de una celda (o fórmula) las flechas
      // siguen moviendo el cursor entre los caracteres.
      const fin = (t.value || "").length;
      if (k === "Escape") hacer(cancelarEdicion);
      else if (k === "Enter") hacer(() => mover(e.shiftKey ? -1 : 1, 0));
      else if (k === "Tab") hacer(() => mover(0, e.shiftKey ? -1 : 1));
      else if (k === "ArrowDown") hacer(() => mover(1, 0));
      else if (k === "ArrowUp") hacer(() => mover(-1, 0));
      else if (k === "ArrowLeft" && t.selectionStart === 0 && t.selectionEnd === 0) hacer(() => mover(0, -1));
      else if (k === "ArrowRight" && t.selectionStart === fin && t.selectionEnd === fin) hacer(() => mover(0, 1));
      return;
    }

    if (ctrl) {
      if (letra === "a") hacer(elegirTodo);
      else if (letra === "b") hacer(() => alternarFormato("bold"));
      else if (letra === "i") hacer(() => alternarFormato("italic"));
      else if (letra === "u") hacer(() => alternarFormato("subrayado"));
      else if (k === "\\") hacer(borrarFormatoSel);
      else if (letra === "d") hacer(rellenarAbajo);
      else if (letra === "f") hacer(() => buscaRef.current && buscaRef.current.focus());
      else if (letra === "h") hacer(() => datos("reemplazar"));
      else if (letra === "p") hacer(() => ver("imprimir"));
      else if (letra === "v" && e.shiftKey) hacer(() => datos("valores"));
      else if (k === "ArrowDown") hacer(() => saltar(1, 0, e.shiftKey));
      else if (k === "ArrowUp") hacer(() => saltar(-1, 0, e.shiftKey));
      else if (k === "ArrowRight") hacer(() => saltar(0, 1, e.shiftKey));
      else if (k === "ArrowLeft") hacer(() => saltar(0, -1, e.shiftKey));
      else if (k === "Home") hacer(() => irA(geo.current.filasNav[0] ?? 0, 0));
      return; // Ctrl+C / X / V llegan como eventos de portapapeles
    }
    if (e.altKey && e.shiftKey && e.code === "Digit5") { hacer(() => alternarFormato("tachado")); return; }
    if (e.altKey) {
      // Alt+↓ abre la lista desplegable de la celda, como en Excel.
      if (k === "ArrowDown" && reglasCalc.listas.has(refDe(s.r, s.c))) hacer(() => api.current.abrirLista(s.r, s.c, t));
      return;
    }

    const pagina = Math.max(1, Math.floor((scroll.alto || 600) / geo.current.RH) - 2);
    if (k === "ArrowDown") hacer(() => mover(1, 0, e.shiftKey));
    else if (k === "ArrowUp") hacer(() => mover(-1, 0, e.shiftKey));
    else if (k === "ArrowRight") hacer(() => mover(0, 1, e.shiftKey));
    else if (k === "ArrowLeft") hacer(() => mover(0, -1, e.shiftKey));
    else if (k === "Enter") hacer(() => mover(e.shiftKey ? -1 : 1, 0));
    else if (k === "Tab") hacer(() => mover(0, e.shiftKey ? -1 : 1));
    else if (k === "PageDown") hacer(() => mover(pagina, 0, e.shiftKey));
    else if (k === "PageUp") hacer(() => mover(-pagina, 0, e.shiftKey));
    else if (k === "Home") hacer(() => mover(0, -geo.current.cols, e.shiftKey));
    else if (k === "End") hacer(() => mover(0, geo.current.cols, e.shiftKey));
    else if (k === "F2" && e.shiftKey) hacer(abrirNota);
    else if (k === "F2") hacer(empezarEdicion);
    else if (k === "Delete") hacer(borrarSeleccion);
    else if (k === "Backspace") hacer(() => { if (esMioRef.current) { empezarEdicion(); setCelda(s.r, s.c, "", `celda:${sesion.current}`); } });
    else if (k === "Escape") { if (brochaRef.current) setBrocha(null); else if (sel2Ref.current) elegir(s, null); }
    // Escribir sobre una celda elegida pisa lo que tenía: se selecciona todo su
    // texto y la letra lo reemplaza sola (así entran también acentos y dictado).
    else if (esTeclaDeTexto(k) && esMioRef.current && t.select) t.select();
  };

  /* Las columnas (o filas) a las que les toca un cambio de tamaño hecho sobre
     la `i`: ella sola, o todas las seleccionadas ENTERAS si ella es una. */
  const enterasCon = (eje, i) => {
    const rg = rangoSel();
    const g = geo.current;
    const out = [i];
    if (!rg) return out;
    if (eje === "col" && rg.r0 === 0 && rg.r1 === g.filas - 1 && rg.c1 > rg.c0 && i >= rg.c0 && i <= rg.c1) {
      for (let c = rg.c0; c <= rg.c1; c += 1) if (c !== i) out.push(c);
    }
    if (eje === "fila" && rg.c0 === 0 && rg.c1 === g.cols - 1 && rg.r1 > rg.r0 && i >= rg.r0 && i <= rg.r1) {
      for (let r = rg.r0; r <= rg.r1; r += 1) if (r !== i) out.push(r);
    }
    return out;
  };

  /* ── Arrastrar el borde de una columna o de una fila ──────────────────────
   *
   * EL ARRASTRE NO PASA POR REACT. Antes cada `mousemove` hacía `setHojas`: el
   * libro cambiaba de identidad, se recalculaban TODAS las fórmulas, se
   * redibujaba el editor entero y se rearmaba el autoguardado — decenas de
   * veces por segundo. Por eso el borde venía atrás del mouse, y más atrás
   * cuanto más grande la planilla.
   *
   * Ahora, mientras se arrastra, se escribe el tamaño directo en el DOM (el
   * `<col>` o el `<tr>`), una vez por cuadro. El estado se toca UNA sola vez,
   * al soltar: ahí queda guardado. Con el ancho de la tabla explícito el
   * navegador usa de verdad el layout fijo, así que mover una columna no remide
   * las demás.
   *
   * Son eventos de puntero, no de mouse: el mismo código anda con el dedo y con
   * lápiz, y la captura hace que el arrastre no se corte al salir del tirador.
   *
   * Con varias columnas (o filas) enteras seleccionadas, arrastrar el borde de
   * una les da ese tamaño a todas. */
  const arrastrar = (eje, i, e) => {
    if (e.button) return; // sólo el botón principal
    e.preventDefault();
    e.stopPropagation();
    const tabla = tablaRef.current;
    const tirador = e.currentTarget;
    if (!tabla) return;
    const esCol = eje === "col";
    const blanco = esCol ? tabla.querySelectorAll("colgroup col")[i + 1] : tirador.closest("tr");
    if (!blanco) return;
    const punto = (ev) => (esCol ? ev.clientX : ev.clientY);
    const p0 = punto(e);
    const propio = esCol ? anchos[i] : altos[i];
    const t0 = propio || (esCol ? ANCHO_COL : (blanco.offsetHeight || filaAlto || 30));
    const minimo = esCol ? ANCHO_MIN : ALTO_MIN;
    const anchoTabla0 = parseFloat(tabla.style.width) || 0;
    const medida = medidaRef.current;
    let t = t0;
    let cuadro = 0;
    let ultimo = e;
    const pintar = () => {
      cuadro = 0;
      if (esCol) {
        blanco.style.width = `${t}px`;
        if (anchoTabla0) tabla.style.width = `${anchoTabla0 - t0 + t}px`;
      } else {
        blanco.style.height = `${t}px`;
      }
      if (medida) {
        medida.textContent = `${t} px`;
        medida.style.left = `${(ultimo.clientX || 0) + 14}px`;
        medida.style.top = `${(ultimo.clientY || 0) + 14}px`;
        medida.hidden = false;
      }
    };
    // Libre al 100%: sin tope de tamaño —una celda puede ocupar casi toda la
    // pantalla si hace falta (Nacho, 24/09)—. Sólo queda el mínimo, para no
    // perder la columna o la fila.
    const calcular = (ev) => {
      if (typeof punto(ev) !== "number") return;
      ultimo = ev;
      t = Math.max(minimo, Math.round(t0 + (punto(ev) - p0) / zoomRef.current));
    };
    const alMover = (ev) => {
      calcular(ev);
      if (!cuadro) cuadro = window.requestAnimationFrame(pintar);
    };
    const terminar = (ev) => {
      window.removeEventListener("pointermove", alMover);
      window.removeEventListener("pointerup", terminar);
      window.removeEventListener("pointercancel", terminar);
      if (cuadro) window.cancelAnimationFrame(cuadro);
      document.body.classList.remove("esp-arrastrando", "esp-arrastrando-fila");
      tirador.classList.remove("activo");
      if (medida) medida.hidden = true;
      // Un arrastre cancelado (el sistema se lo lleva, p. ej. un gesto del
      // teléfono) vuelve al tamaño de antes en vez de dejarlo a medio camino.
      if (ev.type === "pointercancel") t = t0; else calcular(ev);
      if (t === t0) {
        if (esCol) {
          blanco.style.width = `${t0}px`;
          if (anchoTabla0) tabla.style.width = `${anchoTabla0}px`;
        } else {
          blanco.style.height = propio ? `${t0}px` : "";
        }
        return;
      }
      pintar();
      if (medida) medida.hidden = true;
      const cuales = enterasCon(eje, i);
      (esCol ? setAnchosActiva : setAltosActiva)((a) => {
        const n = { ...a };
        for (const j of cuales) n[j] = t;
        return n;
      });
    };
    try { tirador.setPointerCapture(e.pointerId); } catch { /* sin captura igual anda: escucha `window` */ }
    window.addEventListener("pointermove", alMover);
    window.addEventListener("pointerup", terminar);
    window.addEventListener("pointercancel", terminar);
    document.body.classList.add("esp-arrastrando");
    if (!esCol) document.body.classList.add("esp-arrastrando-fila");
    tirador.classList.add("activo");
  };

  /* Doble clic en el borde AJUSTA la columna a su contenido, como en Excel: mide
     el texto más largo (de todas las filas, no sólo las dibujadas) y la deja
     justa. Una columna vacía vuelve al ancho por defecto. */
  const ajustarAncho = (c0) => {
    const tabla = tablaRef.current;
    const medir = medidorDeTexto(tabla && tabla.querySelector("td input"));
    const nuevos = {};
    for (const c of enterasCon("col", c0)) {
      let max = 0;
      for (let r = 0; r < valores.length; r += 1) {
        const f = formato[refDe(r, c)];
        const s = formatear(valores[r][c], f);
        if (s) max = Math.max(max, medir(s) * (f && f.bold ? 1.08 : 1));
      }
      nuevos[c] = max ? Math.min(ANCHO_AJUSTE_MAX, Math.max(ANCHO_MIN, Math.ceil(max) + RELLENO_CELDA)) : null;
    }
    setAnchosActiva((a) => {
      const n = { ...a };
      for (const [c, w] of Object.entries(nuevos)) { if (w) n[c] = w; else delete n[c]; }
      return n;
    });
  };

  /* Doble clic en el borde de abajo del número de fila la vuelve al alto por
     defecto. */
  const resetAlto = (r0) => {
    const cuales = enterasCon("fila", r0);
    setAltosActiva((a) => { const n = { ...a }; for (const r of cuales) delete n[r]; return n; });
  };

  /* Virtualización: mide el alto real de una fila y actualiza el del área
     visible, para dibujar SÓLO las filas a la vista (la grilla puede tener
     cientos de filas y sólo se montan ~20).
     Se queda con la MÁS BAJA que midió: una fila sin alto propio igual puede
     ser más alta (letra grande, un título de un Excel importado), y ese no es
     el alto por defecto. Tomando cada medida tal cual, bajando hasta cierto
     lugar la fila alta corría la ventana a una baja, la baja la volvía a la
     alta, y así sin fin: React cortaba con el error #185 y se caía la
     pantalla (Lautaro, 22/10). Sólo bajando, se asienta en pocas vueltas. */
  const medirFila = useCallback((el) => {
    const alto = el && el.offsetHeight;
    if (alto) setFilaAlto((h) => (h == null || alto < h ? alto : h));
  }, []);
  const onScroll = (e) => setScroll({ top: e.currentTarget.scrollTop, alto: e.currentTarget.clientHeight });
  useEffect(() => {
    const a = wrapRef.current && wrapRef.current.clientHeight;
    if (a) setScroll((s) => (s.alto !== a ? { ...s, alto: a } : s));
  }, [cargado]);

  const agregarFila = () => estructura("fila", geo.current.filas, 1);
  const agregarCol = () => estructura("col", geo.current.cols, 1);

  /* ── Las hojas (pestañas) ────────────────────────────────────────────────── */

  /* Cambiar de hoja arranca limpio: sin selección, sin filtros y arriba de todo
     (cada hoja tiene su propio tamaño). */
  const limpiarVista = () => {
    setEditando(false); elegir(null, null); setFiltros({}); setConfirmandoBorrar(null); setMenu(null);
    setPedidoComb(null); // la pregunta de «Combinar y centrar» era de la otra hoja
    if (wrapRef.current) wrapRef.current.scrollTop = 0;
    setScroll((s) => ({ ...s, top: 0 }));
  };
  const cambiarHoja = (i) => { setActiva(i); limpiarVista(); };

  /* Lo que el asistente de fórmulas (AsistenteFormula.jsx) le pide a la
     planilla: escribir la celda que se edita (como una tecla más: mismo paso de
     deshacer), escribir una celda de otra hoja (la fórmula que se terminó
     mirando otra pestaña) y volver a ella. */
  const escribirFormula = (v) => {
    const s = selRef.current;
    if (s) setCelda(s.r, s.c, v, `celda:${sesion.current}`);
  };
  const escribirEnHoja = (i, r, c, v) => mutar((hs) => hs.map((h, j) => (
    j === i && !(h.celdas[r] && h.celdas[r][c] === v) ? escribir(h, [{ r, c, v }]) : h)));
  const volverACelda = (i, r, c) => {
    if (i !== activaRef.current) cambiarHoja(i);
    elegir({ r, c }, null);
    focoPendiente.current = { r, c };
  };
  const agregarHoja = () => {
    const idx = hojas.length; // la nueva queda al final
    mutar((hs) => [...hs, hojaVacia(`Hoja ${hs.length + 1}`)]);
    cambiarHoja(idx);
  };
  const renombrarHoja = (i, propuesto) => {
    const n = (propuesto || "").trim() || `Hoja ${i + 1}`;
    mutar((hs) => (hs[i] && hs[i].nombre === n ? hs : hs.map((h, j) => (j === i ? { ...h, nombre: n } : h))));
    setEditandoHoja(null);
  };
  const borrarHoja = (i) => {
    if (hojas.length <= 1) return; // siempre queda al menos una
    mutar((hs) => hs.filter((_, j) => j !== i));
    setActiva(activa > i ? activa - 1 : activa === i ? Math.max(0, i - 1) : activa);
    limpiarVista();
  };

  /* Bajar la planilla como Excel: un libro con TODAS las hojas, con sus
     fórmulas, sus formatos de número y sus anchos (ver `shared/planillaExcel`).
     XLSX (SheetJS) se carga acá, sólo al exportar, para no sumarlo al arranque
     del hub. */
  async function exportar() {
    const XLSX = await import("xlsx");
    const wb = exportarLibro(XLSX, hojas, nombreHojaExcel, { hub: hubRef.current });
    XLSX.writeFile(wb, `${(nombre || "planilla").replace(/[\\/:*?"<>|]/g, "-")}.xlsx`);
  }

  /* «Importar Excel como hojas nuevas» (el botón, o soltar el archivo en el
     editor): las hojas del Excel se suman al final de esta planilla, con las
     fórmulas entre ellas apuntando a sus nombres nuevos si alguno chocaba (ver
     `shared/importarExcel`). Es un cambio más: Ctrl+Z lo deshace. */
  async function importarComoHojas(f) {
    if ((f.size ?? 0) > TOPE_IMPORTAR_BYTES) {
      setAvisoPropio(textoArchivoGrande(f.size));
      return;
    }
    try {
      const { hojas: nuevas, aviso: base } = await leerExcel(f);
      if (!esMioRef.current || !hojasRef.current || !nuevas.length) return;
      const desde = hojasRef.current.length;
      const { hojas: todas, agregadas, renombradas } = sumarHojas(hojasRef.current, nuevas);
      mutar(() => todas);
      cambiarHoja(desde);
      setAvisoPropio(avisoDeHojasNuevas(agregadas, renombradas, base));
    } catch {
      setAvisoPropio(LEER_MAL);
    }
  }

  /* «Avisarme por WhatsApp si cambia» sobre lo seleccionado. Nace PRENDIDA
     (la única automatización que nace así: la intención es explícita). La
     hoja va por nombre sólo si el libro tiene varias; con una sola va null
     («la primera»), que sigue andando si se la renombra o se agrega otra. */
  async function avisarmeSiCambia() {
    const s = selRef.current;
    const h = hojasRef.current && hojasRef.current[activaRef.current];
    if (!s || !h || !esMioRef.current || !esDueno) return;
    const g = geo.current;
    const rg = rangoSel();
    const texto = textoDeRango({
      r0: rg.r0, c0: rg.c0,
      r1: Math.min(rg.r1, Math.max(0, g.filas - 1)), c1: Math.min(rg.c1, Math.max(0, g.cols - 1)),
    });
    const nombreHoja = hojasRef.current.length > 1 ? h.nombre : null;
    const yaEsta = rangosVigilados(avisos, id, h.nombre, activaRef.current).some((v) => v.rango === texto);
    if (yaEsta) {
      setAvisoCampana({ tipo: "ok", texto: `Ya te avisamos si cambia ${texto}.` });
      return;
    }
    try {
      const creada = await crearAutomatizacion(avisoDeRango({
        planillaId: id, planillaNombre: nombre, hoja: nombreHoja, rango: texto,
      }));
      if (creada) setAvisos((as) => [...as, creada]);
      // El bot calcula sin la sesión de nadie, así que para él las HUB.… son
      // #HUB (ver `shared/planillaHub`). Se avisa acá, no en el WhatsApp.
      let conHub = false;
      for (let r = rg.r0; r <= Math.min(rg.r1, h.celdas.length - 1) && !conHub; r += 1) {
        for (let c = rg.c0; c <= rg.c1 && !conHub; c += 1) conHub = /HUB\./i.test(String(h.celdas[r][c] ?? ""));
      }
      setAvisoCampana({
        tipo: "ok",
        texto: `Listo: te mandamos un WhatsApp cuando cambie ${texto}. La primera pasada del bot sólo saca la foto; avisa desde el cambio siguiente.`
          + (conHub ? " Ojo: las celdas con datos del hub (HUB.…) el bot no las puede leer —no tiene tu sesión— y le llegan como #HUB." : ""),
      });
    } catch (e) {
      setAvisoCampana({ tipo: "error", texto: `No se pudo armar el aviso${e && e.message ? `: ${e.message}` : "."}` });
    }
  }
  async function apagarAviso(autoId) {
    try {
      await prenderApagar(autoId, false);
      setAvisos((as) => as.filter((a) => a.id !== autoId));
      setAvisoCampana({ tipo: "ok", texto: "Listo: ese aviso quedó apagado." });
    } catch (e) {
      setAvisoCampana({ tipo: "error", texto: `No se pudo apagar${e && e.message ? `: ${e.message}` : "."}` });
    }
  }

  if (!cargado) {
    if (estado === "sin-acceso") return <PlanillaSinAcceso onVolver={onSalir} />;
    return estado === "error"
      ? <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
      : <Loading />;
  }

  const filas = celdas.length;
  const cols = celdas[0] ? celdas[0].length : 0;
  // El ancho total, explícito: sin él `table-layout: fixed` no rige y el
  // navegador vuelve a medir el contenido de todas las celdas en cada cambio.
  let anchoTabla = ANCHO_FILA_NUM;
  for (let c = 0; c < cols; c += 1) anchoTabla += anchos[c] || ANCHO_COL;
  const selOk = sel && sel.r < filas && sel.c < cols ? sel : null;
  const rawSel = selOk ? celdas[selOk.r][selOk.c] : "";
  const fmtSel = (selOk && formato[refDe(selOk.r, selOk.c)]) || {};
  // Cuántos decimales muestra hoy la celda activa: de ahí suman o restan los
  // botones de decimales.
  const textoSel = selOk ? formatear(valores[selOk.r] ? valores[selOk.r][selOk.c] : "", fmtSel) : "";
  const conDecimales = /,(\d+)/.exec(textoSel);
  const decimalesSel = conDecimales ? conDecimales[1].length : 0;

  /* El rango seleccionado y su resumen numérico, tipo la barra de estado de
     Excel. */
  const rango = selOk ? rangoElegido(selOk, sel2, combinadas) : null;
  const esRango = !!rango && (rango.r0 !== rango.r1 || rango.c0 !== rango.c1);
  const enRango = (r, c) =>
    esRango && r >= rango.r0 && r <= rango.r1 && c >= rango.c0 && c <= rango.c1;
  const enRelleno = (r, c) =>
    !!relleno && !!rango && r >= relleno.r0 && r <= relleno.r1 && c >= relleno.c0 && c <= relleno.c1
    && !(r >= rango.r0 && r <= rango.r1 && c >= rango.c0 && c <= rango.c1);
  // Qué haría «Combinar y centrar» sobre lo elegido: combinar, separar, nada…
  const accionComb = rango
    ? queHaceCombinar(combinadas, { ...rango, r1: Math.min(rango.r1, filas - 1), c1: Math.min(rango.c1, cols - 1) }).accion
    : "nada";
  const colEntera = (c) => !!rango && rango.r0 === 0 && rango.r1 >= filas - 1 && c >= rango.c0 && c <= rango.c1;
  const filaEntera = (r) => !!rango && rango.c0 === 0 && rango.c1 >= cols - 1 && r >= rango.r0 && r <= rango.r1;
  let resumenSel = null;
  if (esRango) {
    let suma = 0;
    let cuenta = 0;
    let min = Infinity;
    let max = -Infinity;
    for (let r = rango.r0; r <= Math.min(rango.r1, filas - 1); r += 1) {
      for (let c = rango.c0; c <= Math.min(rango.c1, cols - 1); c += 1) {
        const v = valores[r] ? valores[r][c] : "";
        if (typeof v === "number") { suma += v; cuenta += 1; min = Math.min(min, v); max = Math.max(max, v); }
      }
    }
    if (cuenta) resumenSel = { suma, cuenta, prom: suma / cuenta, min, max };
  }

  /* Filtros: esconden filas que no coinciden. La fila 1 (encabezado) queda
     siempre. Es sólo de vista — no toca los datos ni las fórmulas. */
  const hayFiltros = verFiltros && Object.values(filtros).some((t) => (t || "").trim());
  const visible = (r) => {
    if (!hayFiltros || r === 0) return true;
    return Object.entries(filtros).every(([c, t]) => {
      const q = (t || "").trim().toLowerCase();
      if (!q) return true;
      const v = valores[r] ? valores[r][+c] : "";
      return formatear(v, formato[refDe(r, +c)]).toLowerCase().includes(q);
    });
  };

  /* La ventana de filas a DIBUJAR: de las que pasan el filtro, sólo las que
     caen en el viewport (+ colchón). Arriba y abajo van dos filas-espaciador
     con el alto de las que no se dibujan, para que el scroll no cambie.
     Con la fila 1 fija, esa fila se dibuja en el encabezado y sale de acá. */
  const filasNav = [];
  for (let r = 0; r < filas; r += 1) if (visible(r)) filasNav.push(r);
  const filasVis = fija.fila ? filasNav.filter((r) => r !== 0) : filasNav;
  const RH = filaAlto || 30;
  const COLCHON = 6;
  // GUARDA DE FAST-PATH: si la hoja no tiene ninguna fila con alto propio, la
  // cuenta es la de siempre (alto uniforme RH). Sólo cuando hay filas
  // redimensionadas a mano se pasa a la cuenta por alturas ACUMULADAS.
  const hayAltos = altos && Object.keys(altos).length > 0;
  // Con zoom, lo que se desplazó y lo que entra en pantalla se miden en px de
  // la HOJA: a 200% entra la mitad de filas, a 50% el doble.
  const sTop = scroll.top / (zoom / 100);
  const sAlto = (scroll.alto || 600) / (zoom / 100);
  let desde;
  let hasta;
  let espArriba;
  let espAbajo;
  let offCuerpo = null;
  if (!hayAltos) {
    desde = Math.min(
      Math.max(0, Math.floor(sTop / RH) - COLCHON),
      Math.max(0, filasVis.length - 1),
    );
    hasta = Math.min(filasVis.length, desde + Math.ceil(sAlto / RH) + COLCHON * 2);
    espArriba = desde * RH;
    espAbajo = Math.max(0, (filasVis.length - hasta) * RH);
  } else {
    // Prefijos de alto: off[i] = px acumulados antes de la i-ésima fila visible.
    const off = new Array(filasVis.length + 1);
    off[0] = 0;
    for (let i = 0; i < filasVis.length; i += 1) {
      off[i + 1] = off[i] + (altos[filasVis[i]] || RH);
    }
    offCuerpo = off;
    const fin = sTop + sAlto;
    let d = 0;
    while (d < filasVis.length && off[d + 1] <= sTop) d += 1;
    d = Math.max(0, d - COLCHON);
    let h = d;
    while (h < filasVis.length && off[h] < fin) h += 1;
    h = Math.min(filasVis.length, h + COLCHON);
    desde = d;
    hasta = h;
    espArriba = off[desde];
    espAbajo = Math.max(0, off[filasVis.length] - off[hasta]);
  }
  const ventana = filasVis.slice(desde, hasta);
  // Para medir el alto por defecto, tomar una fila SIN alto propio (si todas las
  // visibles tienen alto custom, no se mide y se mantiene el último).
  const iMedir = ventana.findIndex((r) => !altos[r]);
  geo.current = { filasNav, cuerpo: filasVis, cols, filas, offCuerpo, RH, valores, comb: mapaComb, estilos: reglasCalc.estilos };

  const topFija = ALTO_LETRAS + (verFiltros ? ALTO_FILTRO : 0);
  /* El cartelito al pasar el mouse por una celda HUB.…: el porqué de su error,
     o qué está pasando. Una celda que sólo REFERENCIA a otra con #HUB no trae
     el porqué (el error se propaga como texto): se la manda a mirar la otra. */
  const motivoDe = (r, c, v) => {
    if (v === CARGANDO) return "Trayendo el dato del hub…";
    const m = motivos.get(refDe(r, c));
    if (m) return m;
    return v === ERROR_HUB ? "Usa un dato del hub que no se pudo traer: mirá la celda con la fórmula HUB.…" : null;
  };
  /* Cuánto se baja el texto de una combinada de varias filas centrada en
     vertical para que quede a la mitad del bloque (se dibuja en el tramo de
     arriba: ver `shared/planillaCombinadas`). Cuentan las filas que se ven; con
     la fila 1 fija, la combinada que arranca ahí queda arriba. */
  const bajarEnComb = (r, c, fmt) => {
    if (!fmt || fmt.valign !== "middle") return 0;
    const rg = mapaComb.get(`${r},${c}`);
    if (!rg || rg.r1 === rg.r0 || (fija.fila && rg.r0 === 0)) return 0;
    let total = 0;
    for (let f = rg.r0; f <= rg.r1; f += 1) if (visible(f)) total += altos[f] || RH;
    return Math.max(0, Math.round((total - (altos[rg.r0] || RH)) / 2));
  };
  const celdasDeFila = (r) => celdas[r].map((raw, c) => {
    // Las franjas llegan hasta donde hay datos, no hasta el fondo de la hoja.
    const alt = hoja.alternos && r < usada.filas && c < usada.cols ? colorAlterno(hoja.alternos, r) : null;
    const comb = celdaCombinada(mapaComb, r, c);
    if (comb && comb.oculta) return null;
    if (comb && comb.cont) {
      const { r: ar, c: ac } = comb.ancla;
      return (
        <Celda
          key={c} r={ar} c={ac} cont span={comb.span} tramo={comb.tramo} fmt={formato[refDe(ar, ac)]}
          cond={reglasCalc.estilos.get(refDe(ar, ac))} enRango={enRango(ar, ac)} acc={acc}
          alt={alt} altCab={r === 0}
        />
      );
    }
    const esActiva = !!(selOk && selOk.r === r && selOk.c === c);
    const val = valores[r] ? valores[r][c] : "";
    return (
      <Celda
        key={c} r={r} c={c} raw={raw}
        val={val}
        motivo={typeof val === "string" && val.startsWith("#") ? motivoDe(r, c, val) : null}
        fmt={formato[refDe(r, c)]}
        activa={esActiva} editando={esActiva && editando}
        enRango={enRango(r, c)} enRelleno={enRelleno(r, c)}
        asa={esMio && !editando && !!rango && r === Math.min(rango.r1, filas - 1) && c === Math.min(rango.c1, cols - 1)}
        fija={!!fija.col && c === 0}
        vigilada={vigilados.length > 0 && celdaEnRangos(vigilados, r, c)}
        cond={reglasCalc.estilos.get(refDe(r, c))}
        lista={esMio && reglasCalc.listas.has(refDe(r, c))}
        invalida={reglasCalc.invalidas.get(refDe(r, c)) || null}
        soloLectura={!esMio} acc={acc}
        span={comb ? comb.span : 1} tramo={comb ? comb.tramo : null}
        bajar={comb ? bajarEnComb(r, c, formato[refDe(r, c)]) : 0}
        alt={alt} altCab={r === 0}
      />
    );
  });
  const cabezaDeFila = (r, z) => (
    <th
      scope="row" className={"esp-fila-head" + (filaEntera(r) ? " sel" : "") + (selOk && selOk.r === r ? " actual" : "")}
      style={{ zIndex: z }}
      onMouseDown={(e) => elegirFila(r, e)}
      onContextMenu={(e) => abrirMenu(e, "fila", r)}
    >
      {r + 1}
      {esMio && (
        <span
          className="esp-fila-resize"
          role="separator"
          aria-label={`Alto de la fila ${r + 1}`}
          title="Arrastrá para agrandar o achicar; doble clic para volver al alto normal"
          onPointerDown={(e) => arrastrar("fila", r, e)}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={() => resetAlto(r)}
        />
      )}
    </th>
  );

  /* Las opciones del menú del botón derecho, según sobre qué se abrió. */
  const nFilas = rango ? Math.min(rango.r1, filas - 1) - rango.r0 + 1 : 0;
  const nCols = rango ? Math.min(rango.c1, cols - 1) - rango.c0 + 1 : 0;
  const cuantas = (n, una, varias) => (n === 1 ? una : `${n} ${varias}`);
  const copiarAlSistema = (cortar) => {
    const copia = copiarSeleccion(cortar);
    if (copia) copiarTextoYTabla(copia);
  };
  const pegarDelSistema = (opciones = {}) => {
    const propio = () => { if (portapapeles.current) pegarTexto(portapapeles.current.texto, opciones); };
    if (navigator.clipboard && navigator.clipboard.readText) {
      navigator.clipboard.readText().then((t) => pegarTexto(t, opciones)).catch(propio);
    } else propio();
  };
  /* El menú Ver: zoom, pantalla completa e imprimir. */
  const ver = (accion) => {
    if (accion.startsWith("zoom:")) {
      const z = Number(accion.slice(5));
      setZoom(z);
      try { window.localStorage.setItem(CLAVE_ZOOM, String(z)); } catch { /* sin almacenamiento, vale por esta vez */ }
    } else if (accion === "completa") {
      const el = raizRef.current;
      if (document.fullscreenElement) document.exitFullscreen();
      else if (el && el.requestFullscreen) el.requestFullscreen().catch(() => setAvisoPropio("Este navegador no dejó pasar a pantalla completa."));
    } else if (accion === "imprimir") {
      // La hoja a la vista como se ve (ticket 452): con los filtros puestos,
      // sólo las filas que pasan; con sus colores condicionales. Ver
      // `shared/planillaImprimir`.
      const h = hojasRef.current && hojasRef.current[activaRef.current];
      const g = geo.current;
      if (h) {
        const { html, letraMinima } = papelParaImprimir(h, g.valores, {
          titulo: nombreRef.current, subtitulo: h.nombre, filas: g.filasNav, estilos: g.estilos,
        });
        // Una hoja muy ancha sale con la letra en el piso y celdas en más de
        // un renglón: se avisa arriba, como lo que contesta Datos. Si después
        // se imprime una que entra, ese aviso se va (otro aviso, no).
        setAvisoPropio((a) => (letraMinima ? AVISO_LETRA_MINIMA : (a === AVISO_LETRA_MINIMA ? null : a)));
        imprimirHtml(html);
      }
    }
  };
  /* El menú Datos. Cada acción trabaja sobre lo seleccionado —o sobre toda la
     hoja si hay una sola celda elegida— y dice cuánto cambió. */
  const datos = (accion) => {
    if (accion === "reemplazar") { setReemplazo({ buscar: "", por: "" }); return; }
    if (accion === "valores") { pegarDelSistema({ soloValores: true }); return; }
    const rg = rangoSel();
    if (!rg) return;
    const elegido = rg.r0 !== rg.r1 || rg.c0 !== rg.c1 ? rg : null;
    let dicho = "";
    mutarHoja((h) => {
      if (accion === "espacios") {
        const r = recortarEspacios(h, elegido);
        dicho = r.cuantas ? `Se recortaron los espacios de ${r.cuantas} ${r.cuantas === 1 ? "celda" : "celdas"}.` : "No había espacios de más.";
        return r.hoja;
      }
      if (accion === "duplicados") {
        const r = quitarDuplicados(h, elegido);
        dicho = r.quitadas ? `Se ${r.quitadas === 1 ? "quitó 1 fila repetida" : `quitaron ${r.quitadas} filas repetidas`}.` : "No hay filas repetidas.";
        return r.hoja;
      }
      const r = dividirTexto(h, { ...rg, c1: rg.c0 });
      dicho = r.cuantas
        ? `Se ${r.cuantas === 1 ? "dividió 1 celda" : `dividieron ${r.cuantas} celdas`} por «${r.separador === " " ? "espacio" : r.separador === "\t" ? "tabulador" : r.separador}».`
        : "No encontré por dónde dividir: elegí la columna que tiene el texto junto.";
      return r.hoja;
    });
    setAvisoPropio(dicho);
  };
  const reemplazarTodo = () => {
    if (!reemplazo || !reemplazo.buscar) return;
    const rg = rangoSel();
    const elegido = rg && (rg.r0 !== rg.r1 || rg.c0 !== rg.c1) ? rg : null;
    let cuantas = 0;
    mutarHoja((h) => { const r = reemplazar(h, reemplazo.buscar, reemplazo.por, elegido); cuantas = r.cuantas; return r.hoja; });
    setAvisoPropio(cuantas
      ? `Se ${cuantas === 1 ? "cambió 1 celda" : `cambiaron ${cuantas} celdas`}${elegido ? " de lo seleccionado" : ""}.`
      : `«${reemplazo.buscar}» no está${elegido ? " en lo seleccionado" : " en esta hoja"}.`);
  };
  const conFilas = !!menu && menu.tipo !== "col";
  const conCols = !!menu && menu.tipo !== "fila";
  const opcionesMenu = !menu || !rango ? [] : [
    esMio && ["Cortar", () => copiarAlSistema(true), "Ctrl+X"],
    ["Copiar", () => copiarAlSistema(false), "Ctrl+C"],
    ["Copiar como imagen", copiarComoImagen],
    esMio && ["Pegar", pegarDelSistema, "Ctrl+V"],
    esMio && ["Borrar el contenido", borrarSeleccion, "Supr"],
    esMio && [fmtSel.nota ? "Editar la nota" : "Agregar una nota", abrirNota, "Mayús+F2"],
    esMio && conFilas && null,
    esMio && conFilas && [`Insertar ${cuantas(nFilas, "una fila", "filas")} arriba`, () => estructura("fila", rango.r0, nFilas)],
    esMio && conFilas && [`Insertar ${cuantas(nFilas, "una fila", "filas")} abajo`, () => estructura("fila", rango.r0 + nFilas, nFilas)],
    esMio && conFilas && [`Eliminar ${cuantas(nFilas, `la fila ${rango.r0 + 1}`, "filas")}`, () => { estructura("fila", rango.r0, -nFilas); elegir(selRef.current, null); }],
    esMio && conCols && null,
    esMio && conCols && [`Insertar ${cuantas(nCols, "una columna", "columnas")} a la izquierda`, () => estructura("col", rango.c0, nCols)],
    esMio && conCols && [`Insertar ${cuantas(nCols, "una columna", "columnas")} a la derecha`, () => estructura("col", rango.c0 + nCols, nCols)],
    esMio && conCols && [`Eliminar ${cuantas(nCols, `la columna ${colALetra(rango.c0)}`, "columnas")}`, () => { estructura("col", rango.c0, -nCols); elegir(selRef.current, null); }],
    esMio && conCols && null,
    esMio && conCols && [`Ordenar por ${colALetra(selOk.c)}, de menor a mayor`, () => ordenarPor(true)],
    esMio && conCols && [`Ordenar por ${colALetra(selOk.c)}, de mayor a menor`, () => ordenarPor(false)],
    esMio && null,
    esDueno && ["🔔 Avisarme por WhatsApp si cambia", avisarmeSiCambia],
  ].filter((x) => x !== false);

  /* ── La barra de menús ────────────────────────────────────────────────────
   * Todo lo que la planilla sabe hacer, ordenado por tema. Lo que cambia la
   * planilla sólo lo ve quien puede editarla; mirar, copiar, imprimir y bajar
   * están para todos. */
  const sinCelda = !selOk;
  const filaYCol = selOk ? `${colALetra(selOk.c)}` : "";
  const menus = [
    { titulo: "Archivo", items: [
      { t: "Descargar como Excel (.xlsx)", accion: exportar },
      { t: "Imprimir…", atajo: "Ctrl+P", accion: () => ver("imprimir") },
      null,
      { t: "Volver a Mis planillas", accion: salir },
    ] },
    { titulo: "Editar", items: [
      esMio && { t: "Deshacer", atajo: "Ctrl+Z", accion: deshacer, desactivado: !hist.current.pasado.length },
      esMio && { t: "Rehacer", atajo: "Ctrl+Y", accion: rehacer, desactivado: !hist.current.futuro.length },
      esMio && null,
      esMio && { t: "Cortar", atajo: "Ctrl+X", accion: () => copiarAlSistema(true), desactivado: sinCelda },
      { t: "Copiar", atajo: "Ctrl+C", accion: () => copiarAlSistema(false), desactivado: sinCelda },
      { t: "Copiar como imagen", accion: copiarComoImagen, desactivado: sinCelda },
      esMio && { t: "Pegar", atajo: "Ctrl+V", accion: () => pegarDelSistema(), desactivado: sinCelda },
      esMio && { t: "Pegar sólo los valores", atajo: "Ctrl+Mayús+V", accion: () => datos("valores"), desactivado: sinCelda },
      null,
      { t: "Seleccionar todo", atajo: "Ctrl+A", accion: elegirTodo },
      esMio && { t: "Borrar el contenido", atajo: "Supr", accion: borrarSeleccion, desactivado: sinCelda },
      null,
      { t: "Buscar", atajo: "Ctrl+F", accion: () => buscaRef.current && buscaRef.current.focus() },
      esMio && { t: "Buscar y reemplazar…", atajo: "Ctrl+H", accion: () => datos("reemplazar") },
    ] },
    { titulo: "Ver", items: [
      { t: `Zoom · ${zoom}%`, sub: ZOOMS.map((z) => ({ t: `${z}%`, marcado: z === zoom, accion: () => ver(`zoom:${z}`) })) },
      { t: pantallaCompleta ? "Salir de pantalla completa" : "Pantalla completa", accion: () => ver("completa") },
      null,
      esMio && { t: "Fijar la fila 1", marcado: !!fija.fila, accion: () => alternarFija("fila") },
      esMio && { t: "Fijar la columna A", marcado: !!fija.col, accion: () => alternarFija("col") },
      { t: "Filtros por columna", marcado: verFiltros, accion: () => setVerFiltros((v) => !v) },
    ] },
    esMio && { titulo: "Insertar", items: [
      { t: nFilas > 1 ? `${nFilas} filas arriba` : "Fila arriba", accion: () => estructura("fila", rango.r0, nFilas), desactivado: sinCelda },
      { t: nFilas > 1 ? `${nFilas} filas abajo` : "Fila abajo", accion: () => estructura("fila", rango.r0 + nFilas, nFilas), desactivado: sinCelda },
      { t: nCols > 1 ? `${nCols} columnas a la izquierda` : "Columna a la izquierda", accion: () => estructura("col", rango.c0, nCols), desactivado: sinCelda },
      { t: nCols > 1 ? `${nCols} columnas a la derecha` : "Columna a la derecha", accion: () => estructura("col", rango.c0 + nCols, nCols), desactivado: sinCelda },
      null,
      { t: "Hoja nueva", accion: agregarHoja },
      null,
      { t: "Función", desactivado: sinCelda, sub: ["SUMA", "PROMEDIO", "CONTAR", "MAX", "MIN"].map((fn) => ({ t: fn, accion: () => sumaRapida(fn) })) },
      { t: fmtSel.nota ? "Editar la nota" : "Nota", atajo: "Mayús+F2", accion: abrirNota, desactivado: sinCelda },
    ] },
    esMio && { titulo: "Formato", items: [
      { t: "Número", desactivado: sinCelda, sub: FORMATOS_NUMERO.map(([clave, nom, ejemplo]) => ({
        t: `${nom} · ${ejemplo}`, marcado: (fmtSel.num || "") === clave, accion: () => aplicarFormato({ num: clave || undefined, dec: undefined }),
      })) },
      { t: "Texto", desactivado: sinCelda, sub: [
        { t: "Negrita", atajo: "Ctrl+B", marcado: !!fmtSel.bold, accion: () => alternarFormato("bold") },
        { t: "Itálica", atajo: "Ctrl+I", marcado: !!fmtSel.italic, accion: () => alternarFormato("italic") },
        { t: "Subrayado", atajo: "Ctrl+U", marcado: !!fmtSel.subrayado, accion: () => alternarFormato("subrayado") },
        { t: "Tachado", atajo: "Alt+Mayús+5", marcado: !!fmtSel.tachado, accion: () => alternarFormato("tachado") },
      ] },
      { t: "Alineación", desactivado: sinCelda, sub: [["left", "Izquierda"], ["center", "Centro"], ["right", "Derecha"]].map(([a, nom]) => ({
        t: nom, marcado: fmtSel.align === a, accion: () => aplicarFormato({ align: fmtSel.align === a ? undefined : a }),
      })) },
      { t: "Alineación vertical", desactivado: sinCelda, sub: [["top", "Arriba"], ["middle", "Medio"], ["bottom", "Abajo"]].map(([a, nom]) => ({
        t: nom, marcado: fmtSel.valign === a, accion: () => aplicarFormato({ valign: fmtSel.valign === a ? undefined : a }),
      })) },
      { t: accionComb === "separar" ? "Separar celdas" : "Combinar y centrar", accion: combinarSel, desactivado: sinCelda || accionComb === "nada" },
      { t: "Tamaño de letra", desactivado: sinCelda, sub: TAMANOS.map((t) => ({
        t: String(t), marcado: (fmtSel.size || 13) === t, accion: () => aplicarFormato({ size: t === 13 ? undefined : t }),
      })) },
      { t: "Bordes", desactivado: sinCelda, sub: TIPOS_BORDE.map(([clave, nom]) => ({ t: nom, accion: () => bordesSel(clave) })) },
      null,
      { t: "Colores alternos", sub: [
        ...Object.entries(ALTERNOS).map(([clave, j]) => ({ t: j.nombre, marcado: hoja.alternos === clave, accion: () => colores(clave) })),
        null,
        { t: "Sin franjas", accion: () => colores("ninguno"), desactivado: !hoja.alternos },
      ] },
      null,
      { t: brocha ? "Soltar la brocha" : "Copiar formato", accion: tomarBrocha, desactivado: sinCelda && !brocha },
      { t: "Borrar formato", atajo: "Ctrl+\\", accion: borrarFormatoSel, desactivado: sinCelda },
    ] },
    esMio && { titulo: "Datos", items: [
      { t: `Ordenar la hoja por ${filaYCol || "la columna"}, de menor a mayor`, accion: () => ordenarPor(true), desactivado: sinCelda },
      { t: `Ordenar la hoja por ${filaYCol || "la columna"}, de mayor a menor`, accion: () => ordenarPor(false), desactivado: sinCelda },
      null,
      { t: "Quitar filas repetidas", accion: () => datos("duplicados"), desactivado: sinCelda },
      { t: "Recortar espacios", accion: () => datos("espacios"), desactivado: sinCelda },
      { t: "Dividir texto en columnas", accion: () => datos("dividir"), desactivado: sinCelda },
    ] },
    { titulo: "Ayuda", items: [
      { t: "Atajos y fórmulas", accion: () => {
        const el = ayudaRef.current;
        if (!el) return;
        el.open = true;
        if (el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      } },
    ] },
  ].filter(Boolean).map((m) => ({ ...m, items: m.items.filter((x) => x !== false) }));

  /* Lo que va en la franja fija de arriba (`.esp-flotantes`): lo que no se
     guardó, lo de otro que choca con lo tuyo sin guardar, lo que te contesta
     una acción (se cruza con otra combinada, hojas nuevas, Datos, reemplazar)
     y la pregunta de «Combinar y centrar». El «se actualizó sola» queda en el
     flujo: no pide nada, y con una automatización que guarda seguido taparía
     arriba a cada rato. */
  const sinGuardar = esMio && !!ESTADO_MALO[estado];
  const vivoUrgente = !!enVivo.aviso && enVivo.aviso.tipo === PENDIENTE;
  const preguntaComb = esMio && pedidoComb && pedidoComb.hoja === hoja ? pedidoComb : null;
  const hayFlotantes = sinGuardar || vivoUrgente || !!(avisoPropio || aviso) || !!preguntaComb || !!avisoImagen;

  return (
    <div className={"docs esp-editor" + (pantallaCompleta ? " esp-completa" : "")} ref={raizRef}>
      <BarraMenus menus={menus} />
      <div className="esp-acciones">
        <button className="rto-link" onClick={salir}>← Mis planillas</button>
        <input
          aria-label="Nombre de la planilla" value={nombre} readOnly={!esMio}
          className="esp-nombre"
          onChange={(e) => { setNombre(e.target.value); onCambioNombre?.(id, e.target.value); }}
        />
        {esMio && (
          <span className={"esp-guardado" + (ESTADO_MALO[estado] ? " mal" : "")} aria-live="polite">
            {TEXTO_ESTADO[estado] || "Guardado"}
          </span>
        )}
        <input
          ref={buscaRef} className={"esp-busca" + (sinResultado ? " vacio" : "")} type="search"
          aria-label="Buscar en la hoja" placeholder="Buscar… (Enter: siguiente)"
          title={sinResultado ? "No está en esta hoja" : "Buscar en la hoja (Ctrl+F)"}
          value={busca}
          onChange={(e) => { setBusca(e.target.value); setSinResultado(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buscarSiguiente(); } }}
        />
        <button
          className={"esp-btn" + (verFiltros ? " sel" : "")}
          onClick={() => setVerFiltros((v) => !v)} title="Filtrar columnas"
        >🔎 Filtros</button>
        <button className="esp-btn" onClick={exportar} title="Bajar como Excel (.xlsx)">⬇ Excel</button>
        <button className="esp-btn" onClick={() => ver("imprimir")} title="Imprimir la hoja (Ctrl+P)">🖨 Imprimir</button>
        <button
          className="esp-btn" onClick={copiarComoImagen} disabled={!selOk}
          // No se lleva el foco: después de copiar, el teclado sigue en la celda.
          onMouseDown={(e) => e.preventDefault()}
          title={selOk ? "Copia lo elegido como una imagen, para pegarla en WhatsApp" : "Elegí las celdas que querés pasar como imagen"}
        >📷 Copiar como imagen</button>
        {esMio && (
          <>
            <InsertarTablaHub
              hoja={hoja} celda={selOk} datos={datosDelHub} alElegir={elegir}
              alCambiar={(fn) => mutar((hs) => fn(hs, activaRef.current))}
            />
            <BotonImportarExcel
              onArchivo={importarComoHojas} etiqueta="Importar Excel como hojas nuevas"
              titulo="Suma las hojas de un Excel a esta planilla (también podés arrastrar el archivo)"
            >⬆ Importar Excel como hojas nuevas</BotonImportarExcel>
          </>
        )}
      </div>
      <SoltarExcel activo={esMio} onArchivo={importarComoHojas} detalle="Sus hojas se suman a esta planilla" />

      {/* La franja fija de arriba (ver `.esp-flotantes` en espacio.css): fuera
          de lo que se desplaza y por encima de la grilla. Con `panel-aviso` a
          secas —la tapa del hub, absoluta y con z-index 1— la pregunta de
          «Combinar y centrar» quedaba debajo de la fila de las letras
          (Lautaro, 19/10); y en el flujo, arriba de la barra, un aviso no se
          veía con la página bajada hasta la grilla. */}
      {hayFlotantes && (
        <div className="esp-flotantes">
          {sinGuardar && (
            <div className="esp-flotante mal esp-sin-guardar" role="alert">
              <span>
                {estado === "conflicto" && <>Esta planilla cambió en otro lado (otra pestaña o una automatización). Recargá para ver lo último.</>}
                {estado === "grande" && <>La planilla pasó el tope de {TOPE_PLANILLA_MB} MB y <b>lo que cambiaste desde el último guardado no se está guardando</b>. Borrá filas u hojas que no uses para que vuelva a guardar.</>}
                {estado === "error" && <>No se pudo guardar lo último (¿se cortó internet?). Seguí acá y probá de nuevo.</>}
                {estado === "error-recarga" && <>No se pudo recargar la planilla. Probá de nuevo en un rato.</>}
              </span>
              {(estado === "conflicto" || estado === "error-recarga") && (
                <button className="esp-btn" onClick={recargar}>Recargar</button>
              )}
              {estado === "conflicto" && (
                <button
                  className="esp-btn" onClick={() => guardarYa({ pisar: true })}
                  title="Pisa lo que cambió en otro lado con lo que ves acá"
                >Guardar mi versión igual (borra lo otro)</button>
              )}
              {estado === "error" && <button className="esp-btn" onClick={() => guardarYa()}>Reintentar</button>}
              {quiereSalir && <button className="esp-btn" onClick={salirSinGuardar}>Salir sin guardar</button>}
            </div>
          )}

          {vivoUrgente && <AvisoEnVivo aviso={enVivo.aviso} onCerrar={enVivo.cerrarAviso} />}

          {(avisoPropio || aviso) && (
            <p className="esp-flotante" role="note">
              <span>{avisoPropio || aviso}</span>
              <button
                className="esp-btn chico" aria-label="Cerrar el aviso"
                onClick={() => { if (avisoPropio) setAvisoPropio(null); else if (onCerrarAviso) onCerrarAviso(); }}
              >Entendido</button>
            </p>
          )}

          {avisoImagen && (
            <p className="esp-flotante" role="note">
              <span>{avisoImagen.texto}</span>
              <a className="esp-btn chico" href={avisoImagen.url} download={avisoImagen.nombre}>Descargar la imagen</a>
              <button className="esp-btn chico" aria-label="Cerrar el aviso de la imagen" onClick={soltarImagen}>Cerrar</button>
            </p>
          )}

          {/* El foco va a «Combinar igual» (Enter combina) y Escape cancela. */}
          {preguntaComb && (
            <div
              className="esp-flotante" role="alertdialog" aria-label="Combinar celdas"
              aria-describedby="esp-pregunta-combinar"
              onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); noCombinar(); } }}
            >
              <span id="esp-pregunta-combinar">{textoAlCombinar(preguntaComb.rango, preguntaComb.seBorran)}</span>
              <button className="esp-btn" autoFocus onClick={combinarIgual}>Combinar igual</button>
              <button className="esp-btn" onClick={noCombinar}>Cancelar</button>
            </div>
          )}
        </div>
      )}

      {!vivoUrgente && <AvisoEnVivo aviso={enVivo.aviso} onCerrar={enVivo.cerrarAviso} />}

      {!esMio && (
        <p className="panel-aviso suave">
          Te compartió esta planilla <b>{meta.dueno}</b>. Podés mirarla, pero no
          editarla — es de quien la creó.
        </p>
      )}
      {esMio && !esDueno && (
        <p className="panel-aviso suave">
          Te compartió esta planilla <b>{meta.dueno}</b> y <b>podés editarla</b>:
          se guarda sola. Compartirla o borrarla lo decide su dueño.
        </p>
      )}

      {esDueno && (
        <CompartirPanel
          compartidaCon={meta.compartidaCon} yo={yo} editores={meta.editores}
          link={linkDePlanilla(id)}
          onGuardar={(...cambios) => guardarCompartir(id, ...cambios)}
        />
      )}

      {cargado && (
        <HistorialPlanilla
          planillaId={id} esDueno={esMio}
          prepararRestaurar={listoParaRestaurar} onRestaurada={recargar}
        />
      )}

      {esMio && (
        <Herramientas
          hay={!!selOk} act={fmtSel} decimales={decimalesSel} aplicar={aplicarFormato}
          puedeDeshacer={hist.current.pasado.length > 0} puedeRehacer={hist.current.futuro.length > 0}
          deshacer={deshacer} rehacer={rehacer}
          bordes={bordesSel} brocha={brocha} tomarBrocha={tomarBrocha}
          combinar={combinarSel} comb={accionComb}
          avisar={esDueno ? avisarmeSiCambia : null}
        >
          <BotonesReglas hay={!!selOk} abierto={panelReglas} onAbrir={setPanelReglas} />
        </Herramientas>
      )}

      {esMio && notaEdit && (
        <form
          className="esp-reemplazo" aria-label="Nota de la celda"
          onSubmit={(e) => { e.preventDefault(); guardarNota(notaEdit.texto); }}
          onKeyDown={(e) => { if (e.key === "Escape") setNotaEdit(null); }}
        >
          <b>{refDe(notaEdit.r, notaEdit.c)}</b>
          <input
            autoFocus aria-label="Texto de la nota" placeholder="Escribí la nota" value={notaEdit.texto} style={{ maxWidth: 520 }}
            onChange={(e) => setNotaEdit({ ...notaEdit, texto: e.target.value })}
          />
          <button className="esp-btn chico" type="submit">Guardar</button>
          <button className="esp-btn chico" type="button" onClick={() => guardarNota("")}>Quitar la nota</button>
          <button className="esp-btn chico" type="button" onClick={() => setNotaEdit(null)} aria-label="Cerrar la nota">✕</button>
        </form>
      )}

      {esMio && reemplazo && (
        <form
          className="esp-reemplazo" aria-label="Buscar y reemplazar"
          onSubmit={(e) => { e.preventDefault(); reemplazarTodo(); }}
          onKeyDown={(e) => { if (e.key === "Escape") setReemplazo(null); }}
        >
          <input
            autoFocus aria-label="Buscar" placeholder="Buscar" value={reemplazo.buscar}
            onChange={(e) => setReemplazo({ ...reemplazo, buscar: e.target.value })}
          />
          <input
            aria-label="Reemplazar por" placeholder="Reemplazar por" value={reemplazo.por}
            onChange={(e) => setReemplazo({ ...reemplazo, por: e.target.value })}
          />
          <button className="esp-btn chico" type="submit" disabled={!reemplazo.buscar}>Reemplazar todo</button>
          <span className="tenue">
            {esRango ? "En lo seleccionado." : "En toda la hoja."} No toca las fórmulas.
          </span>
          <button className="esp-btn chico" type="button" onClick={() => setReemplazo(null)} aria-label="Cerrar buscar y reemplazar">✕</button>
        </form>
      )}

      {esMio && panelReglas && (
        <PanelReglas
          cual={panelReglas} hoja={hoja}
          rango={rango ? { ...rango, r1: Math.min(rango.r1, filas - 1), c1: Math.min(rango.c1, cols - 1) } : null}
          onCambiar={(campo, lista) => mutarHoja((h) => ({ ...h, [campo]: lista }))}
          onCerrar={() => setPanelReglas(null)}
        />
      )}

      {esDueno && (vigilados.length > 0 || avisoCampana) && (
        <div className="esp-avisos" aria-live="polite">
          {vigilados.length > 0 && (
            <>
              <span>🔔 Te avisamos por WhatsApp si cambia:</span>
              {vigilados.map((v) => (
                <span key={v.id} className="esp-aviso-rango">
                  <b>{v.rango}</b>
                  <IrA to={`/automatizaciones/${v.id}`}>ver</IrA>
                  <button className="esp-btn chico" onClick={() => apagarAviso(v.id)} aria-label={`Apagar el aviso de ${v.rango}`}>Apagar</button>
                </span>
              ))}
            </>
          )}
          {avisoCampana && (
            <span className={avisoCampana.tipo === "error" ? "esp-aviso-error" : "tenue"}>
              {avisoCampana.texto}
              <button className="esp-btn chico" onClick={() => setAvisoCampana(null)} aria-label="Cerrar el mensaje">×</button>
            </span>
          )}
        </div>
      )}

      <div className="esp-barra">
        <span className="ref">
          {!selOk ? "—" : esRango
            ? `${refDe(rango.r0, rango.c0)}:${refDe(Math.min(rango.r1, filas - 1), Math.min(rango.c1, cols - 1))}`
            : refDe(selOk.r, selOk.c)}
        </span>
        <AsistenteFormula
          activo={esMio} barraRef={barraRef} hojas={hojas.map((h) => h.nombre)} activa={activa} sel={selOk}
          escribir={escribirFormula} escribirEn={escribirEnHoja} volverA={volverACelda}
        />
        <input
          ref={barraRef} aria-label="Contenido de la celda" value={rawSel} disabled={!selOk} readOnly={!esMio}
          placeholder="Escribí un valor o una fórmula: =SUMA(A1:A3)" spellCheck={false} autoComplete="off"
          onFocus={() => { sesion.current += 1; setEditando(false); }}
          onChange={(e) => selOk && setCelda(selOk.r, selOk.c, e.target.value, `celda:${sesion.current}`)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); mover(1, 0); }
            else if (e.key === "Escape") { e.preventDefault(); cancelarEdicion(); if (selOk) irA(selOk.r, selOk.c); }
          }}
        />
      </div>

      <div
        className={"esp-grilla-wrap" + (brocha ? " esp-con-brocha" : "")} ref={wrapRef} tabIndex={-1} onScroll={onScroll}
        onKeyDown={alTeclear}
        onCopy={(e) => alCopiar(e, false)} onCut={(e) => alCopiar(e, true)} onPaste={alPegar}
        onContextMenu={(e) => { if (esCeldaDeGrilla(e.target)) abrirMenu(e, "celda"); }}
      >
        <table className="esp-grilla" ref={tablaRef} style={{ width: anchoTabla, zoom: zoom !== 100 ? zoom / 100 : undefined }}>
          <colgroup>
            <col style={{ width: ANCHO_FILA_NUM }} />
            {Array.from({ length: cols }, (_, c) => (
              <col key={c} style={{ width: anchos[c] || ANCHO_COL }} />
            ))}
          </colgroup>
          <thead>
            <tr className="esp-letras">
              <th
                className="esp-esquina" style={{ zIndex: Z_ENCABEZADO + cols * 2 + 2 }}
                title="Seleccionar todo" aria-label="Seleccionar todo"
                onMouseDown={(e) => { if (!e.button) { e.preventDefault(); elegirTodo(); } }}
              ></th>
              {Array.from({ length: cols }, (_, c) => (
                <th
                  key={c}
                  className={"esp-col-head" + (colEntera(c) ? " sel" : "") + (selOk && selOk.c === c ? " actual" : "") + (fija.col && c === 0 ? " fija-col" : "")}
                  style={{ zIndex: Z_ENCABEZADO + cols - c + (fija.col && c === 0 ? cols + 1 : 0) }}
                  onMouseDown={(e) => elegirColumna(c, e)}
                  onContextMenu={(e) => abrirMenu(e, "col", c)}
                >
                  {colALetra(c)}
                  {esMio && (
                    <span
                      className="esp-col-resize"
                      role="separator"
                      aria-label={`Ancho de la columna ${colALetra(c)}`}
                      title="Arrastrá para agrandar o achicar; doble clic la ajusta al contenido"
                      onPointerDown={(e) => arrastrar("col", c, e)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDoubleClick={() => ajustarAncho(c)}
                    />
                  )}
                </th>
              ))}
            </tr>
            {verFiltros && (
              <tr className="esp-filtro-fila">
                <th aria-hidden="true" style={{ zIndex: Z_ENCABEZADO + cols * 2 + 2 }}></th>
                {Array.from({ length: cols }, (_, c) => (
                  <th
                    key={c} className={fija.col && c === 0 ? "fija-col" : ""}
                    style={{ zIndex: Z_ENCABEZADO + (fija.col && c === 0 ? cols * 2 + 1 : 0) }}
                  >
                    <input
                      className="esp-filtro-input" value={filtros[c] || ""}
                      placeholder="filtrar…" aria-label={`Filtrar ${colALetra(c)}`}
                      onChange={(e) => setFiltros((f) => ({ ...f, [c]: e.target.value }))}
                    />
                  </th>
                ))}
              </tr>
            )}
            {fija.fila && filas > 0 && (
              <tr
                className="esp-fila-fija"
                style={{ "--esp-top-fija": `${topFija}px`, ...(altos[0] ? { height: altos[0] } : {}) }}
              >
                {cabezaDeFila(0, Z_ENCABEZADO - 1)}
                {celdasDeFila(0)}
              </tr>
            )}
          </thead>
          <tbody>
            {espArriba > 0 && (
              <tr aria-hidden="true"><td colSpan={cols + 1} style={{ height: espArriba, padding: 0, border: 0 }} /></tr>
            )}
            {ventana.map((r, i) => (
              <tr key={r} ref={i === iMedir ? medirFila : undefined}
                  style={altos[r] ? { height: altos[r] } : undefined}>
                {cabezaDeFila(r, Z_FILA + ventana.length - i)}
                {celdasDeFila(r)}
              </tr>
            ))}
            {espAbajo > 0 && (
              <tr aria-hidden="true"><td colSpan={cols + 1} style={{ height: espAbajo, padding: 0, border: 0 }} /></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="esp-medida" ref={medidaRef} hidden aria-hidden="true" />

      {listaAbierta && reglasCalc.listas.has(refDe(listaAbierta.r, listaAbierta.c)) && (
        <MenuLista
          {...listaAbierta}
          opciones={reglasCalc.listas.get(refDe(listaAbierta.r, listaAbierta.c)).opciones}
          actual={formatear(valores[listaAbierta.r] ? valores[listaAbierta.r][listaAbierta.c] : "")}
          onElegir={(v) => {
            const { r, c } = listaAbierta;
            setListaAbierta(null);
            setCelda(r, c, v);
            irA(r, c);
          }}
          onCerrar={() => setListaAbierta(null)}
        />
      )}

      {menu && opcionesMenu.length > 0 && (
        <div
          className="esp-menu" role="menu"
          style={{
            left: Math.max(4, Math.min(menu.x, window.innerWidth - 290)),
            top: Math.max(4, Math.min(menu.y, window.innerHeight - 34 * opcionesMenu.length - 12)),
          }}
          // Ni cierra el menú (el clic afuera lo cierra) ni le saca el foco a la
          // celda: después de elegir una opción, el teclado sigue en la grilla.
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onContextMenu={(e) => e.preventDefault()}
        >
          {opcionesMenu.map((op, i) => (op === null
            ? <hr key={i} />
            : (
              <button key={i} role="menuitem" onClick={() => { setMenu(null); op[1](); }}>
                <span>{op[0]}</span>
                {op[2] && <kbd>{op[2]}</kbd>}
              </button>
            )))}
        </div>
      )}

      {/* Las pestañas del libro (como Excel). Doble clic renombra; la × borra la
          hoja activa con una confirmación en línea (nada de diálogos nativos).
          A la derecha, el resumen de lo seleccionado. */}
      <div className="esp-pie">
        <div className="esp-hojas" role="tablist" aria-label="Hojas">
          {hojas.map((h, i) => {
            if (editandoHoja === i) {
              return (
                <input
                  key={i} className="esp-hoja-edit" autoFocus defaultValue={h.nombre}
                  aria-label="Nombre de la hoja"
                  onBlur={(e) => renombrarHoja(i, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); renombrarHoja(i, e.currentTarget.value); }
                    else if (e.key === "Escape") setEditandoHoja(null);
                  }}
                />
              );
            }
            if (confirmandoBorrar === i) {
              return (
                <span key={i} className="esp-hoja confirmando">
                  ¿Borrar?
                  <button className="esp-hoja-si" onClick={() => borrarHoja(i)} aria-label={`Confirmar borrar ${h.nombre}`}>Sí</button>
                  <button className="esp-hoja-no" onClick={() => setConfirmandoBorrar(null)}>No</button>
                </span>
              );
            }
            return (
              <button
                key={i} role="tab" aria-selected={i === activa}
                className={"esp-hoja" + (i === activa ? " activa" : "")}
                onClick={() => cambiarHoja(i)}
                onDoubleClick={() => esMio && setEditandoHoja(i)}
                title={esMio ? `${h.nombre} — doble clic para renombrar` : h.nombre}
              >
                <span className="esp-hoja-n">{h.nombre}</span>
                {esMio && hojas.length > 1 && i === activa && (
                  <span
                    className="esp-hoja-x" role="button" tabIndex={0}
                    aria-label={`Borrar hoja ${h.nombre}`}
                    onClick={(e) => { e.stopPropagation(); setConfirmandoBorrar(i); }}
                  >×</span>
                )}
              </button>
            );
          })}
          {esMio && (
            <button className="esp-hoja-add" onClick={agregarHoja} aria-label="Agregar hoja" title="Agregar hoja">+</button>
          )}
        </div>

        {resumenSel && (
          <div className="esp-estado" role="status">
            <span>Suma <b>{formatear(Math.round(resumenSel.suma * 100) / 100)}</b></span>
            <span>Promedio <b>{formatear(Math.round(resumenSel.prom * 100) / 100)}</b></span>
            <span>Cuenta <b>{resumenSel.cuenta}</b></span>
            <span>Mín <b>{formatear(resumenSel.min)}</b></span>
            <span>Máx <b>{formatear(resumenSel.max)}</b></span>
          </div>
        )}
      </div>

      {esMio && (
        <div className="esp-acciones">
          <button className="esp-btn" onClick={agregarFila}>+ Fila</button>
          <button className="esp-btn" onClick={agregarCol}>+ Columna</button>
          {esDueno && (
            <button
              className="esp-btn" style={{ marginLeft: "auto", color: "var(--crit, #c0392b)" }}
              onClick={() => {
                // Va a la papelera (se restaura desde la lista de Mi espacio).
                if (!window.confirm(`¿Mandar la planilla "${nombre}" a la papelera? La podés restaurar desde «Papelera», en Mis planillas, durante 30 días.`)) return;
                // Borrada, no hay nada pendiente que guardar al salir.
                const G = guardado.current;
                clearTimeout(G.timer);
                G.soltada = true;
                borrarPlanilla(id).then(onSalir).catch(() => { G.soltada = false; setEstado("error"); });
              }}
            >
              Borrar planilla
            </button>
          )}
        </div>
      )}

      <details className="esp-ayuda" ref={ayudaRef}>
        <summary>Atajos y fórmulas</summary>
        <p>
          <b>Moverse:</b> flechas, Enter, Tab; con <kbd>Ctrl</kbd> salta al final
          de los datos. <b>Seleccionar:</b> arrastrando, con <kbd>Shift</kbd> +
          flechas, o tocando la letra de la columna, el número de la fila o la
          esquina (todo). <b>Editar:</b> escribir pisa la celda; doble clic o
          {" "}<kbd>F2</kbd> la corrige; <kbd>Esc</kbd> la deja como estaba;
          {" "}<kbd>Supr</kbd> borra lo seleccionado.
        </p>
        <p>
          <kbd>Ctrl+Z</kbd> deshace y <kbd>Ctrl+Y</kbd> rehace. <kbd>Ctrl+C</kbd>,
          {" "}<kbd>Ctrl+X</kbd> y <kbd>Ctrl+V</kbd> copian, cortan y pegan —
          también desde y hacia Excel. El cuadradito de la esquina de la
          selección, arrastrado, <b>rellena</b>: copia fórmulas y sigue series
          (1, 2, 3… · fechas · lunes, martes…). <kbd>Ctrl+D</kbd> copia hacia
          abajo. Con el <b>botón derecho</b>: insertar y eliminar filas y
          columnas, ordenar.
        </p>
        <p>
          <b>Fórmulas</b> (empiezan con <b>=</b>): referencias <b>A1</b>, fijas
          {" "}<b>$A$1</b>, rangos <b>A1:A10</b> o enteros <b>A:A</b>, otra hoja <b>Enero!A1</b> o
          {" "}<b>'Mi Hoja'!A1</b>, porcentaje <b>=A1*21%</b>, texto con
          {" "}<b>&amp;</b>. Funciones: <b>SUMA, PROMEDIO, MIN, MAX, CONTAR,
          CONTARA, MEDIANA, REDONDEAR, SI, Y, O, SI.ERROR, SI.CONJUNTO, SUMAR.SI,
          CONTAR.SI, SUMAR.SI.CONJUNTO, CONTAR.SI.CONJUNTO, BUSCARV, BUSCARX,
          INDICE, COINCIDIR, CONCATENAR, EXTRAE, IZQUIERDA, DERECHA, LARGO,
          SUSTITUIR, TEXTO, HOY, DIAS, FECHA, FIN.MES, DIAS.LAB, SIFECHA, HORA,
          MINUTO, NSHORA</b> y más, también con su nombre en inglés. Comodines
          {" "}<b>*</b> y <b>?</b> en CONTAR.SI y compañía (<b>"Pe*"</b>); las horas
          {" "}<b>08:30</b> se suman y se restan. Se guarda solo.
        </p>
        <p>
          <b>Datos del hub</b>: traen el dato vivo, con lo que tu permiso te
          deja ver (mientras viaja, la celda muestra «…»; si no se puede, dice
          {" "}<b>#HUB</b> y al pasar el mouse explica por qué). Los avisos por
          WhatsApp no los pueden leer.
        </p>
        <ul className="esp-ayuda-hub">
          {FUNCIONES_HUB.map((f) => (
            <li key={f.nombre}>
              <code>{f.firma}</code> — {f.descripcion} <span className="tenue">Ej.: <code>{f.ejemplo}</code></span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

/* ── El índice + el ruteo interno ──────────────────────────────────────────── */

const MiEspacio = () => {
  const [lista, setLista] = useState(null);
  const [yo, setYo] = useState(null);
  // El link directo (`?planilla=<id>`, FiboSheets #6) la abre de entrada.
  const [abierta, setAbierta] = useState(() => idDelLink());
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null); // lo que pasó con las fórmulas al importar
  const [errorImportar, setErrorImportar] = useState(null); // por qué no se pudo importar
  const [plantillas, setPlantillas] = useState(false); // el selector de «Nueva desde plantilla»
  const importarRef = useRef(null);

  const recargar = useCallback(() => {
    misPlanillas().then(setLista).catch((e) => setError(e.message || "No se pudo leer"));
  }, []);
  useEffect(() => {
    miMail().then((m) => setYo((m || "").toLowerCase())).catch(() => setYo(""));
    recargar();
  }, [recargar]);
  // La barra de direcciones dice qué planilla está abierta: copiarla o
  // recargar la pestaña vuelve a la misma. Al irse de Mi espacio se saca,
  // para que volver no reabra la última sin que nadie la pida.
  useEffect(() => { marcarEnLaUrl(abierta); }, [abierta]);
  useEffect(() => () => marcarEnLaUrl(null), []);

  const nueva = async () => {
    try {
      const id = await crearPlanilla("Sin título", empaquetarLibro([hojaVacia("Hoja 1")], 0));
      setAbierta(id);
    } catch (e) {
      setError(e.message || "No se pudo crear");
    }
  };

  /* Importar un Excel/CSV a una planilla NUEVA (así no pisa nada): con el
     botón o arrastrando el archivo a la pantalla. Trae TODAS las hojas con sus
     fórmulas, formatos, colores, combinadas y anchos de columna; lo que se
     hace con cada cosa —y el aviso de qué no se pudo traer tal cual— está en
     `shared/importarExcel`. */
  async function importarArchivo(f) {
    setErrorImportar(null);
    // Un archivo enorme ni se abre: leerlo en el navegador lo cuelga.
    if ((f.size ?? 0) > TOPE_IMPORTAR_BYTES) {
      setErrorImportar(textoArchivoGrande(f.size));
      return;
    }
    let leido;
    try {
      leido = await leerExcel(f);
    } catch {
      setErrorImportar(LEER_MAL);
      return;
    }
    // Leerlo anduvo; lo que puede fallar ahora es guardarlo. Un Excel que pasa
    // el tope de una planilla lo dice con su tamaño, no «no pude leer».
    const libro = leido.hojas.length ? leido.hojas : [hojaVacia("Hoja 1")];
    const datos = empaquetarLibro(libro, 0);
    try {
      const id = await crearPlanilla(nombreDeArchivo(f.name), datos);
      setAviso(leido.aviso);
      setAbierta(id);
    } catch (e) {
      setErrorImportar(e && e.codigo === MUY_GRANDE
        ? textoMuyGrande(datos)
        : `No se pudo crear la planilla: ${(e && e.message) || "algo falló"}. Probá de nuevo.`);
    }
  }
  function onImportar(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    e.target.value = ""; // permitir reimportar el mismo archivo
    importarArchivo(f);
  }

  if (abierta) {
    // Por el link directo se llega antes de saber quién soy: sin eso no se
    // sabe si es mía, compartida para editar o para ver.
    if (yo === null) return <Loading />;
    return (
      <Editor
        key={abierta} id={abierta} yo={yo} aviso={aviso} onCerrarAviso={() => setAviso(null)}
        onSalir={() => { setAbierta(null); setAviso(null); recargar(); }}
        onCambioNombre={(id, nombre) =>
          setLista((l) => (l || []).map((p) => (p.id === id ? { ...p, nombre } : p)))}
      />
    );
  }

  if (error) return <div className="panel-aviso"><h3>No se pudo consultar</h3></div>;
  if (lista === null || yo === null) return <Loading />;

  const mias = lista.filter((p) => (p.dueno || "").toLowerCase() === yo);
  const compartidas = lista.filter((p) => (p.dueno || "").toLowerCase() !== yo);

  const Item = ({ p, deQuien }) => {
    const puedeEditar = deQuien && rolEnPlanilla(p, yo) === EDITOR;
    return (
      <div className="esp-item" onClick={() => setAbierta(p.id)}>
        <b>{p.nombre || "Sin título"}</b>
        <span className="tenue">
          {deQuien ? `de ${deQuien}` : (p.actualizado_en ? new Date(p.actualizado_en).toLocaleDateString("es-AR") : "")}
          {deQuien && (
            <span className={"esp-rol" + (puedeEditar ? " editar" : "")}>
              {puedeEditar ? "Editor" : "Solo lectura"}
            </span>
          )}
        </span>
      </div>
    );
  };

  return (
    <div className="docs">
      <div className="esp-acciones">
        <h2 style={{ margin: 0, flex: 1 }}>Mis planillas</h2>
        <button className="esp-btn" onClick={() => importarRef.current && importarRef.current.click()}>
          ⬆ Importar Excel
        </button>
        <input
          ref={importarRef} type="file" accept={ACEPTA}
          style={{ display: "none" }} aria-label="Importar Excel" onChange={onImportar}
        />
        <button className="esp-btn" onClick={() => setPlantillas((v) => !v)}>📋 Nueva desde plantilla</button>
        <button className="btn-primario" onClick={nueva}>+ Nueva planilla</button>
      </div>
      <SoltarExcel onArchivo={importarArchivo} detalle="Se crea una planilla nueva con todas sus hojas" />
      {errorImportar && (
        <div className="panel-aviso esp-aviso" role="alert">
          <span>{errorImportar}</span>
          <button className="esp-btn chico" onClick={() => setErrorImportar(null)} aria-label="Cerrar el aviso">×</button>
        </div>
      )}
      {/* Los Excel que le mandaron al bot (FiboSheets #10). */}
      <ImportacionesBot onImportadas={recargar} onAbrir={setAbierta} />
      {plantillas && (
        <PlantillasPlanilla
          onCreada={(id) => { setPlantillas(false); setAbierta(id); }}
          onCerrar={() => setPlantillas(false)}
        />
      )}

      {mias.length === 0 ? (
        <p className="panel-aviso suave">
          Todavía no tenés ninguna planilla.
        </p>
      ) : (
        <div className="esp-lista">
          {mias.map((p) => <Item key={p.id} p={p} />)}
        </div>
      )}

      {compartidas.length > 0 && (
        <>
          <h3 style={{ margin: "22px 0 6px" }}>Compartidas conmigo</h3>
          <div className="esp-lista">
            {compartidas.map((p) => <Item key={p.id} p={p} deQuien={p.dueno} />)}
          </div>
        </>
      )}

      <PapeleraPlanillas onRestaurada={recargar} />
    </div>
  );
};

export default MiEspacio;
